import { subscribeBrief, reconciliationDelay, type BriefSnapshot } from '../realtime';
"use client";

import { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo, type CSSProperties } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

import svgPaths from "../imports/svg-n7w9hotcin";
import svgInputPaths from "../imports/svg-tdzfjk767x";
import svgHelpPaths from "../imports/svg-xpfbbj0czi";
import { useSession } from './SessionDropdown';
import { useLanguage, isSupportedLanguage } from './LanguageDropdown';
import {
  fetchSessionById,
  fetchSessionSiteBriefTask,
  updateMessageLikeStatus,
  updateMessageDislikeStatus,
  deleteMessageById,
  fetchWebliumWebsites,
  fetchWebliumWebsitePages,
  fetchWebliumWebsiteBlocks,
  fetchWebliumWebsiteBlockElements,
  fetchDecisionsBySessionId,
  fetchDecisionGeneratedImageBlob,
  acceptDecisionById,
  declineDecisionById,
  setDecisionUserInputById,
  setDecisionUpgradeRequiredStateById,
  setMessageOfferPlanUpgradeStateById,
  setDecisionWebsitePublishStateById
} from '../api';
import type {
  completionFunctionalToolsCallsLogType,
  SessionContextType,
  SiteBriefTaskType,
  SiteBriefChatMetadata,
  SessionSummary,
  SessionRenameOptions,
  SessionUsageInfo,
  MentionItemType,
  MessageMentionsType,
  WebliumWebsiteMentionItem,
  WebliumWebsitePageMentionItem,
  WebliumWebsiteBlockMentionItem,
  WebliumWebsiteElementMentionItem,
  DecisionListItemType,
  MessageDecisionReferenceType,
  MessageRouterType,
  DecisionValueType,
  DecisionWebsitePublishStateType,
  UpgradeRequiredType,
  OfferPlanUpgradeType,
  VibeBlockDesignWizardAnswerType,
  VibeBlockDesignWizardQuestionType
} from '../api.types';
import { formatMessageDuration, formatMessageTimestamp } from '../utils/chatFormatters';
import markdown2html from '../utils/markdown2html';
import '../styles/custom-code-editor.css';
import { highlightCustomCodeContent, type CustomCodeHighlightMode } from '../utils/customCodeHighlight';
import VibeBlockDesignWizardCard from './VibeBlockDesignWizardCard';

type MessageActionKey = 'like' | 'dislike' | 'delete';
type MessageActionLoadingMap = Record<string, Record<MessageActionKey, boolean>>;
type DecisionActionKey = 'accept' | 'decline' | 'userInput' | 'publish';
type DecisionActionLoadingMap = Record<string, Record<DecisionActionKey, boolean>>;
type DecisionImagePreviewStateType = {
  status: 'idle' | 'loading' | 'ready' | 'error';
  url?: string;
};
type PlanUpgradeResolutionScope = {
  websiteId: string;
  websiteOwnerId: string;
  stateUpdatedAt?: string;
};

import {
  chatFlow,
  ChatFlowMessage,
  UserMessage,
  HelpMessage,
  ProceedMessage,
  Message,
  ChatActions,
  ImageData,
  handleUserResponse as chatHandleUserResponse,
  handleSkipQuestion as chatHandleSkipQuestion,
  handleHelpClick as chatHandleHelpClick,
  handleHelpSuggestionClick as chatHandleHelpSuggestionClick,
  handleProceed as chatHandleProceed,
  fileToImageData,
  isImageFile,
  isImageSizeValid,
  MAX_IMAGE_UPLOAD_SIZE_LABEL
} from '../chat';

type VibeBlockDesignWizardStepProgressType = {
  wizard: VibeBlockDesignWizardQuestionType;
  messageId: Message['id'];
  messageIndex: number;
  answer?: VibeBlockDesignWizardAnswerType;
};

type VibeBlockDesignWizardProgressType = {
  wizardId: string;
  firstMessageIndex: number;
  latestMessageIndex: number;
  latestMessageId: Message['id'];
  steps: VibeBlockDesignWizardStepProgressType[];
  latestStep: VibeBlockDesignWizardStepProgressType;
  isComplete: boolean;
  isSkipped: boolean;
};

const buildVibeBlockDesignWizardProgress = (messages: Message[]): VibeBlockDesignWizardProgressType[] => {
  const wizardGroups = new Map<string, {
    firstMessageIndex: number;
    stepsByNumber: Map<number, Omit<VibeBlockDesignWizardStepProgressType, 'answer'>>;
  }>();

  messages.forEach((message, messageIndex) => {
    const wizard = message.vibe_block_design_wizard_question;
    if (!wizard) return;

    const existingGroup = wizardGroups.get(wizard.wizard_id);
    const group = existingGroup || {
      firstMessageIndex: messageIndex,
      stepsByNumber: new Map<number, Omit<VibeBlockDesignWizardStepProgressType, 'answer'>>()
    };

    group.firstMessageIndex = Math.min(group.firstMessageIndex, messageIndex);
    group.stepsByNumber.set(wizard.step, { wizard, messageId: message.id, messageIndex });
    wizardGroups.set(wizard.wizard_id, group);
  });

  return Array.from(wizardGroups.entries())
    .map(([wizardId, group]) => {
      const steps = Array.from(group.stepsByNumber.values())
        .sort((left, right) => left.wizard.step - right.wizard.step)
        .map((step): VibeBlockDesignWizardStepProgressType => {
          const confirmedAnswer = messages
            .slice(step.messageIndex + 1)
            .find((candidate) => (
              candidate.type === 'user'
              && candidate.vibeBlockDesignWizardAnswerStatus === 'confirmed'
              && candidate.vibe_block_design_wizard_answer?.wizard_id === wizardId
              && candidate.vibe_block_design_wizard_answer.step === step.wizard.step
            ))
            ?.vibe_block_design_wizard_answer;

          return { ...step, answer: confirmedAnswer };
        });
      const latestStep = steps[steps.length - 1];
      if (!latestStep) return null;

      return {
        wizardId,
        firstMessageIndex: group.firstMessageIndex,
        latestMessageIndex: latestStep.messageIndex,
        latestMessageId: latestStep.messageId,
        steps,
        latestStep,
        isComplete: latestStep.wizard.step === latestStep.wizard.total_steps
          && Boolean(latestStep.answer)
          && latestStep.answer?.skip !== true,
        isSkipped: latestStep.answer?.skip === true
      };
    })
    .filter((progress): progress is VibeBlockDesignWizardProgressType => Boolean(progress))
    .sort((left, right) => left.firstMessageIndex - right.firstMessageIndex);
};

const shouldFormatAsHtml = (message: Message): boolean => {
  return message.type !== 'user' && message.type !== 'error';
};

const formatMessageHtml = (message: Message, overrideContent?: string): string => {
  if (!message || typeof (overrideContent ?? message.content) !== 'string') {
    return '';
  }

  if (!shouldFormatAsHtml(message)) {
    return '';
  }

  return markdown2html(overrideContent ?? message.content);
};

const getWebsiteMentionId = (mentions?: MessageMentionsType): string | null => {
  if (!mentions?.website || typeof mentions.website.id !== 'string') {
    return null;
  }
  return mentions.website.id;
};

const formatWebsiteMentionFallback = (websiteId: string): string => {
  if (!websiteId) {
    return 'Website';
  }
  if (websiteId.length <= 12) {
    return websiteId;
  }
  return `${websiteId.slice(0, 6)}...${websiteId.slice(-4)}`;
};

const formatPageMentionFallback = (pageId: string): string => {
  if (!pageId) {
    return 'Page';
  }
  if (pageId.length <= 12) {
    return pageId;
  }
  return `${pageId.slice(0, 6)}...${pageId.slice(-4)}`;
};

const formatBlockMentionFallback = (blockId: string): string => {
  if (!blockId) {
    return 'Block';
  }
  if (blockId.length <= 12) {
    return blockId;
  }
  return `${blockId.slice(0, 6)}...${blockId.slice(-4)}`;
};

const formatElementMentionFallback = (elementId: string): string => {
  if (!elementId) {
    return 'Element';
  }
  if (elementId.length <= 12) {
    return elementId;
  }
  return `${elementId.slice(0, 6)}...${elementId.slice(-4)}`;
};

const formatMentionId = (id: string): string => {
  if (!id) {
    return '-';
  }
  return id;
};

const formatDecisionLabel = (decisionDetails?: DecisionListItemType): string => {
  if (!decisionDetails || typeof decisionDetails.decision_label !== 'string') {
    return 'Decision';
  }

  const normalizedLabel = decisionDetails.decision_label.trim();
  return normalizedLabel.length > 0 ? normalizedLabel : 'Decision';
};

const formatDecisionStateLabel = (state: MessageDecisionReferenceType['state']): string => {
  if (state === 'autoApply') return 'Automatically applied';
  if (state === 'userOwnValueSet') return 'User value set';
  if (state === 'userOwnValueAccepted') return 'User value accepted';
  if (state === 'userOwnValueDeclined') return 'User value declined';
  if (state === 'outdated') return 'Outdated';
  if (state === 'accepted') return 'Accepted';
  if (state === 'declined') return 'Declined';
  return 'Created';
};

const UPGRADE_REQUIRED_TITLE = 'Go Pro for Full Access';
const UPGRADE_REQUIRED_DESCRIPTION = 'Your current plan does not include this capability. Upgrade to Pro to remove limits, unlock premium features, and experience our most powerful AI models.';
const CHAT_INPUT_MIN_HEIGHT = 64;
const CHAT_INPUT_LINE_HEIGHT = 24;
const CHAT_INPUT_VERTICAL_PADDING = 32;
const CHAT_INPUT_MAX_ROWS = 5;

const getSteppedChatInputHeight = (scrollHeight: number): number => {
  const contentRows = Math.max(
    1,
    Math.ceil((scrollHeight - CHAT_INPUT_VERTICAL_PADDING) / CHAT_INPUT_LINE_HEIGHT)
  );
  const visibleRows = Math.min(CHAT_INPUT_MAX_ROWS, contentRows);

  return CHAT_INPUT_MIN_HEIGHT + ((visibleRows - 1) * CHAT_INPUT_LINE_HEIGHT);
};

const getModalErrorMessage = (error: unknown, fallbackMessage: string): string => {
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }

  return fallbackMessage;
};

const isRecordValue = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};

const getDecisionUpgradeRequired = (details?: DecisionListItemType): UpgradeRequiredType | null => {
  if (!details) {
    return null;
  }

  if (isRecordValue(details.upgradeRequired)) {
    return details.upgradeRequired;
  }

  if (isRecordValue(details.upgrade_required)) {
    return details.upgrade_required;
  }

  if (isRecordValue(details.is_upgrade_required)) {
    return details.is_upgrade_required;
  }

  if (details.is_upgrade_required === true) {
    return {};
  }

  return null;
};

const isUpgradeRequiredActive = (
  upgradeRequired: UpgradeRequiredType | null,
  isUpgradeRequiredReference: boolean
): boolean => {
  if (!upgradeRequired) {
    return isUpgradeRequiredReference;
  }

  const state = upgradeRequired.state;
  return state !== 'planUpgraded' && state !== 'upgradePlanPopupNotApplicable';
};

const shouldShowUpgradeButton = (upgradeRequired: UpgradeRequiredType | null): boolean => {
  if (!upgradeRequired || !isRecordValue(upgradeRequired.options)) {
    return typeof upgradeRequired?.kind === 'string' && upgradeRequired.kind === 'userNeedsUpgradePlan';
  }

  const options = upgradeRequired.options as Record<string, unknown>;

  if (typeof options.is_show_upgrade_button === 'boolean') {
    return options.is_show_upgrade_button;
  }

  if (typeof options.isShowUpgradeButton === 'boolean') {
    return options.isShowUpgradeButton;
  }

  return false;
};

const isOfferPlanUpgradeActive = (offerPlanUpgrade?: OfferPlanUpgradeType): boolean => {
  if (!offerPlanUpgrade) {
    return false;
  }

  return offerPlanUpgrade.state !== 'planUpgraded' &&
    offerPlanUpgrade.state !== 'upgradePlanPopupNotApplicable';
};

const shouldShowOfferPlanUpgradeButton = (offerPlanUpgrade?: OfferPlanUpgradeType): boolean => {
  if (!offerPlanUpgrade) {
    return false;
  }

  return offerPlanUpgrade.kind === 'userNeedsUpgradePlan';
};

const getRecordStringValue = (value: unknown, key: string): string | null => {
  if (!isRecordValue(value)) {
    return null;
  }

  const rawValue = value[key];
  return typeof rawValue === 'string' && rawValue.length > 0 ? rawValue : null;
};

const getOfferPlanUpgradeWebsiteId = (offerPlanUpgrade?: OfferPlanUpgradeType): string | null => {
  if (!offerPlanUpgrade) {
    return null;
  }

  return offerPlanUpgrade.website_id || offerPlanUpgrade.websiteId || null;
};

const getOfferPlanUpgradeWebsiteOwnerId = (offerPlanUpgrade?: OfferPlanUpgradeType): string | null => {
  if (!offerPlanUpgrade) {
    return null;
  }

  return offerPlanUpgrade.website_owner_id || offerPlanUpgrade.websiteOwnerId || null;
};

const getUpgradeRequiredWebsiteId = (upgradeRequired: UpgradeRequiredType | null): string | null => {
  return getRecordStringValue(upgradeRequired, 'websiteId') ||
    getRecordStringValue(upgradeRequired, 'website_id');
};

const getUpgradeRequiredWebsiteOwnerId = (upgradeRequired: UpgradeRequiredType | null): string | null => {
  return getRecordStringValue(upgradeRequired, 'websiteOwnerId') ||
    getRecordStringValue(upgradeRequired, 'website_owner_id');
};

const getUpgradeRequiredStateUpdatedAt = (upgradeRequired: UpgradeRequiredType | null): string | undefined => {
  return getRecordStringValue(upgradeRequired, 'stateUpdatedAt') ||
    getRecordStringValue(upgradeRequired, 'state_updated_at') ||
    undefined;
};

const buildPlanUpgradeResolutionScopeFromOffer = (
  offerPlanUpgrade?: OfferPlanUpgradeType
): PlanUpgradeResolutionScope | null => {
  const websiteId = getOfferPlanUpgradeWebsiteId(offerPlanUpgrade);
  const websiteOwnerId = getOfferPlanUpgradeWebsiteOwnerId(offerPlanUpgrade);

  if (!websiteId || !websiteOwnerId) {
    return null;
  }

  return {
    websiteId,
    websiteOwnerId,
    stateUpdatedAt: offerPlanUpgrade?.state_updated_at || offerPlanUpgrade?.stateUpdatedAt
  };
};

const buildPlanUpgradeResolutionScopeFromDecision = (
  decision: DecisionListItemType
): PlanUpgradeResolutionScope | null => {
  const upgradeRequired = getDecisionUpgradeRequired(decision);
  const websiteId = getUpgradeRequiredWebsiteId(upgradeRequired);
  const websiteOwnerId = getUpgradeRequiredWebsiteOwnerId(upgradeRequired);

  if (!websiteId || !websiteOwnerId) {
    return null;
  }

  return {
    websiteId,
    websiteOwnerId,
    stateUpdatedAt: getUpgradeRequiredStateUpdatedAt(upgradeRequired)
  };
};

const isOfferPlanUpgradeInScope = (
  offerPlanUpgrade: OfferPlanUpgradeType | undefined,
  scope: PlanUpgradeResolutionScope
): offerPlanUpgrade is OfferPlanUpgradeType => {
  return getOfferPlanUpgradeWebsiteId(offerPlanUpgrade) === scope.websiteId &&
    getOfferPlanUpgradeWebsiteOwnerId(offerPlanUpgrade) === scope.websiteOwnerId;
};

const isUpgradeRequiredInScope = (
  upgradeRequired: UpgradeRequiredType | null,
  scope: PlanUpgradeResolutionScope
): upgradeRequired is UpgradeRequiredType => {
  return getUpgradeRequiredWebsiteId(upgradeRequired) === scope.websiteId &&
    getUpgradeRequiredWebsiteOwnerId(upgradeRequired) === scope.websiteOwnerId;
};

const isDecisionFinalState = (state: MessageDecisionReferenceType['state']): boolean => {
  return state === 'accepted' ||
    state === 'autoApply' ||
    state === 'declined' ||
    state === 'outdated' ||
    state === 'userOwnValueAccepted' ||
    state === 'userOwnValueDeclined';
};

const shouldShowHiddenForEndUserBanner = (
  reference: MessageDecisionReferenceType,
  details?: DecisionListItemType
): boolean => {
  return details?.annotations?.hide_for_enduser === true ||
    reference.annotations?.hide_for_enduser === true;
};

const isAutoApplyDecision = (
  reference: MessageDecisionReferenceType,
  details?: DecisionListItemType
): boolean => {
  return details?.annotations?.auto_apply_decision === true ||
    reference.annotations?.auto_apply_decision === true ||
    (details?.state ?? reference.state) === 'autoApply';
};

const getDecisionWebsitePublishState = (
  details?: DecisionListItemType,
  reference?: MessageDecisionReferenceType
): DecisionWebsitePublishStateType => {
  return details?.website_publish_state
    ?? reference?.website_publish_state
    ?? 'not_applicable';
};

const getDecisionPublishButtonLabel = (
  websitePublishState: DecisionWebsitePublishStateType,
  isLoading: boolean
): string => {
  if (websitePublishState === 'published') {
    return 'Published';
  }

  if (isLoading || websitePublishState === 'publishing') {
    return 'Publishing';
  }

  return 'Publish';
};

const wait = (ms: number): Promise<void> => {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
};

const MAX_DECISION_SHORT_TEXT_LENGTH = 255;
const MAX_DECISION_LONG_TEXT_LENGTH = 20000;
const CUSTOM_CODE_DECISION_SECTIONS = new Set(['head', 'afterBody', 'beforeBody', 'css']);
const EMBED_CODE_DECISION_TYPES = new Set(['pageEmbedCodeBlock', 'pageVibeCodeBlock']);
const EMBED_CODE_DECISION_SECTIONS = new Set(['html', 'css', 'js']);

const normalizeEmbedCodeDecisionSection = (decisionSubType: string): 'html' | 'css' | 'js' | null => {
  const normalizedSubType = decisionSubType.trim();

  if (normalizedSubType === 'cssLess') {
    return 'css';
  }

  if (EMBED_CODE_DECISION_SECTIONS.has(normalizedSubType)) {
    return normalizedSubType as 'html' | 'css' | 'js';
  }

  return null;
};

const getCustomCodeDecisionMode = (details: DecisionListItemType): CustomCodeHighlightMode | null => {
  if (details.decisionType !== 'globalCustomCode') {
    return null;
  }

  if (!CUSTOM_CODE_DECISION_SECTIONS.has(details.decisionSubType)) {
    return null;
  }

  return details.decisionSubType === 'css' ? 'css' : 'html-mixed';
};

const getEmbedCodeDecisionMode = (details: DecisionListItemType): CustomCodeHighlightMode | null => {
  if (!EMBED_CODE_DECISION_TYPES.has(details.decisionType)) {
    return null;
  }

  const decisionSection = normalizeEmbedCodeDecisionSection(details.decisionSubType);
  if (!decisionSection) {
    return null;
  }

  if (decisionSection === 'css') {
    return 'css';
  }

  if (decisionSection === 'js') {
    return 'js';
  }

  return decisionSection === 'html' ? 'html-mixed' : null;
};

type DecisionUserInputModalState = {
  decisionId: string;
  decisionTypeLabel: string;
  valueType: DecisionValueType;
  booleanValue: boolean;
  numberValue: string;
  shortTextValue: string;
  longTextValue: string;
  error: string | null;
};

type UpgradeRequiredModalState = {
  decisionId: string;
  isSubmitting: boolean;
  activeAction: 'cancel' | 'confirm' | null;
  error: string | null;
};

type OfferPlanUpgradeModalState = {
  messageId: string;
  isSubmitting: boolean;
  activeAction: 'cancel' | 'confirm' | null;
  error: string | null;
};

const buildDecisionUserInputModalState = (
  decision: MessageDecisionReferenceType,
  details: DecisionListItemType
): DecisionUserInputModalState => {
  return {
    decisionId: decision.id,
    decisionTypeLabel: formatDecisionLabel(details),
    valueType: details.valueType,
    booleanValue: typeof details.value_boolean === 'boolean' ? details.value_boolean : true,
    numberValue: typeof details.value_number === 'number' ? details.value_number.toString() : '',
    shortTextValue: typeof details.value_short_text === 'string' ? details.value_short_text : '',
    longTextValue: typeof details.value_long_text === 'string' ? details.value_long_text : '',
    error: null
  };
};

type DecisionMetadataItem = {
  key: 'websiteId' | 'pageId' | 'blockId' | 'decisionGroupId' | 'decisionGroupName';
  label: 'Website ID' | 'Page ID' | 'Block ID' | 'Decision Group ID' | 'Decision Group label';
  value: string;
};

const getDecisionMetadataItems = (details?: DecisionListItemType): DecisionMetadataItem[] => {
  if (!details) {
    return [];
  }

  const metadataItems: DecisionMetadataItem[] = [];

  if (typeof details.websiteId === 'string' && details.websiteId.trim().length > 0) {
    metadataItems.push({
      key: 'websiteId',
      label: 'Website ID',
      value: details.websiteId
    });
  }

  if (typeof details.pageId === 'string' && details.pageId.trim().length > 0) {
    metadataItems.push({
      key: 'pageId',
      label: 'Page ID',
      value: details.pageId
    });
  }

  if (typeof details.blockId === 'string' && details.blockId.trim().length > 0) {
    metadataItems.push({
      key: 'blockId',
      label: 'Block ID',
      value: details.blockId
    });
  }

  if (typeof details.decisionGroupId === 'string' && details.decisionGroupId.trim().length > 0) {
    metadataItems.push({
      key: 'decisionGroupId',
      label: 'Decision Group ID',
      value: details.decisionGroupId
    });
  }

  if (typeof details.decisionGroupName === 'string' && details.decisionGroupName.trim().length > 0) {
    metadataItems.push({
      key: 'decisionGroupName',
      label: 'Decision Group label',
      value: details.decisionGroupName
    });
  }

  return metadataItems;
};

const hasDecisionGroupMetadata = (details?: DecisionListItemType): boolean => {
  if (!details) {
    return false;
  }

  return (
    (typeof details.decisionGroupId === 'string' && details.decisionGroupId.trim().length > 0)
    || (typeof details.decisionGroupName === 'string' && details.decisionGroupName.trim().length > 0)
  );
};

const getDecisionAcceptButtonLabel = (details: DecisionListItemType | undefined, isLoading: boolean): string => {
  if (isLoading) {
    return 'Accepting...';
  }

  return hasDecisionGroupMetadata(details) ? 'Group Accept' : 'Accept';
};

const getDecisionDeclineButtonLabel = (details: DecisionListItemType | undefined, isLoading: boolean): string => {
  if (isLoading) {
    return 'Declining...';
  }

  return hasDecisionGroupMetadata(details) ? 'Group Decline' : 'Decline';
};

const copyTextToClipboard = async (text: string): Promise<boolean> => {
  try {
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      await navigator.clipboard.writeText(text);
      return true;
    }

    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'absolute';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
    return true;
  } catch (error) {
    console.error('Failed to copy text to clipboard', error);
    return false;
  }
};

const getWebsiteDomainLabel = (website: WebliumWebsiteMentionItem): string => {
  return website.domainLabel;
};

type MentionTypeIconProps = {
  entityType: 'Website' | 'Page' | 'Block' | 'Element';
  isHomePage?: boolean;
  className?: string;
};

function MentionTypeIcon({ entityType, isHomePage, className = 'size-3' }: MentionTypeIconProps) {
  if (entityType === 'Page') {
    if (isHomePage) {
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 10.5 12 3l9 7.5" />
          <path d="M5 9.5V21h14V9.5" />
        </svg>
      );
    }

    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M7 3h8l4 4v14H7z" />
        <path d="M15 3v5h5" />
      </svg>
    );
  }

  if (entityType === 'Block') {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="4" y="4" width="7" height="7" rx="1.4" />
        <rect x="13" y="4" width="7" height="7" rx="1.4" />
        <rect x="4" y="13" width="7" height="7" rx="1.4" />
        <rect x="13" y="13" width="7" height="7" rx="1.4" />
      </svg>
    );
  }

  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="8" />
      <path d="M12 7 17 12 12 17 7 12Z" />
      <circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" />
    </svg>
  );
}

type MentionEntityRowProps = {
  entityType: 'Website' | 'Page' | 'Block' | 'Element';
  name: string;
  id: string;
  isHomePage?: boolean;
  copyKey: string;
  copiedMentionKey: string | null;
  onCopy: (value: string, copyKey: string) => void;
};

function MentionEntityRow({
  entityType,
  name,
  id,
  isHomePage,
  copyKey,
  copiedMentionKey,
  onCopy
}: MentionEntityRowProps) {
  const isCopied = copiedMentionKey === copyKey;
  const tooltipLabel = isCopied ? 'Copied' : 'Copy ID';
  const shouldRenderIcon = entityType === 'Page' || entityType === 'Block' || entityType === 'Element';
  const mentionIconLabel = entityType === 'Page' ? (isHomePage ? 'Home page' : 'Page') : entityType;

  return (
    <div className="flex items-start justify-between gap-2 rounded-xl border border-[#d9e6ff] bg-white px-2.5 py-2">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="rounded-md bg-[#eef4ff] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-[#2f5ca8]">
            {entityType}
          </span>
          {shouldRenderIcon && (
            <span
              className="inline-flex size-4 items-center justify-center rounded bg-[#eaf2ff] text-[#2f5ca8]"
              title={mentionIconLabel}
              aria-label={mentionIconLabel}
            >
              <MentionTypeIcon entityType={entityType} isHomePage={isHomePage} />
            </span>
          )}
          <span className="truncate text-[13px] font-semibold text-[#27344a]" title={name}>
            {name}
          </span>
        </div>
        <div className="mt-1 break-all font-mono text-[11px] text-[#5f6f89]" title={id}>
          ID: {formatMentionId(id)}
        </div>
      </div>
      <button
        type="button"
        className="group relative shrink-0 rounded-lg border border-[#d7e3ff] bg-[#f8fbff] p-1.5 text-[#4f6ea8] transition-colors hover:border-[#97b9ff] hover:text-[#165BFB]"
        onClick={() => onCopy(id, copyKey)}
        title={tooltipLabel}
        aria-label={`Copy ${entityType.toLowerCase()} mention ID`}
      >
        {isCopied ? (
          <svg className="size-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 6 9 17l-5-5" />
          </svg>
        ) : (
          <svg className="size-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
          </svg>
        )}
        <span className="pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md bg-[#1f2937] px-2 py-1 text-[10px] font-medium text-white opacity-0 transition-opacity group-hover:opacity-100">
          {tooltipLabel}
        </span>
      </button>
    </div>
  );
}

const formatRouterConfidence = (confidence: number): string => {
  if (!Number.isFinite(confidence)) {
    return '-';
  }

  return Number.isInteger(confidence) ? String(confidence) : confidence.toFixed(3).replace(/0+$/, '').replace(/\.$/, '');
};

type MessageRouterPanelProps = {
  router?: MessageRouterType;
  isLoading?: boolean;
};

function MessageRouterPanel({ router, isLoading = false }: MessageRouterPanelProps) {
  const groups = Array.isArray(router?.groups)
    ? router.groups.filter((group): group is string => typeof group === 'string' && group.trim().length > 0)
    : [];
  const reason = typeof router?.reason === 'string' ? router.reason.trim() : '';
  const classifierModel = typeof router?.classifierModel === 'string' ? router.classifierModel.trim() : '';
  const hasConfidence = typeof router?.confidence === 'number' && Number.isFinite(router.confidence);
  const hasSelectedTools = Array.isArray(router?.selected_tools);
  const selectedTools = hasSelectedTools
    ? router.selected_tools.filter((tool): tool is string => typeof tool === 'string')
    : [];
  const selectedToolsCount = selectedTools.length;
  const [isSelectedToolsExpanded, setIsSelectedToolsExpanded] = useState(false);
  const hasRouter = groups.length > 0 || reason.length > 0 || classifierModel.length > 0 || hasConfidence || hasSelectedTools;

  return (
    <div className={`agent-router mt-3 rounded-2xl border p-3 ${
      isLoading
        ? 'border-[#fde68a] bg-[#fffbeb]'
        : hasRouter
        ? 'border-[#ccdcff] bg-[#f7faff]'
        : 'border-[#d8e1f5] bg-[#f3f6fc]'
    }`}>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#4f6ea8]">
          Router
        </div>
        <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.06em] ${
          isLoading
            ? 'bg-[#fff7d6] text-[#b45309]'
            : hasRouter
            ? 'bg-[#e6f0ff] text-[#2457cc]'
            : 'bg-white text-[#7b879b]'
        }`}>
          {isLoading ? 'loading...' : hasRouter ? 'Set' : 'Not set'}
        </span>
      </div>

      {isLoading ? (
        <div className="rounded-xl border border-[#fde68a] bg-white px-3 py-2 text-left text-[12px] leading-5 text-[#92400e]">
          Waiting for completion router...
        </div>
      ) : hasRouter ? (
        <div className="space-y-2">
          <div className="rounded-xl border border-[#d9e6ff] bg-white px-3 py-2">
            <div className="mb-1 flex flex-wrap items-center justify-between gap-2 text-[10px] font-semibold uppercase tracking-[0.06em] text-[#4f6ea8]">
              <span>Groups</span>
            </div>
            {groups.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {groups.map((group, groupIndex) => (
                  <span
                    key={`${group}-${groupIndex}`}
                    className="rounded-full bg-[#eef4ff] px-2 py-0.5 text-[11px] font-semibold text-[#2f5ca8]"
                    title={group}
                  >
                    {group}
                  </span>
                ))}
              </div>
            ) : (
              <div className="text-[12px] text-[#7b879b]">
                -
              </div>
            )}
          </div>

          <div className="rounded-xl border border-[#d9e6ff] bg-white px-3 py-2">
            <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
              <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[#4f6ea8]">
                Selected tools ({selectedToolsCount})
              </div>
              {hasSelectedTools && (
                <button
                  type="button"
                  className="rounded-full border border-[#cbdcff] bg-[#f5f8ff] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-[#2f5ca8] transition-colors hover:bg-[#edf3ff]"
                  onClick={() => setIsSelectedToolsExpanded((prevState) => !prevState)}
                  aria-expanded={isSelectedToolsExpanded}
                  title={isSelectedToolsExpanded ? 'Hide selected tools' : 'Show selected tools'}
                >
                  {isSelectedToolsExpanded ? 'Hide' : 'Show'}
                </button>
              )}
            </div>
            {hasSelectedTools && isSelectedToolsExpanded && selectedTools.length > 0 && (
              <div className="space-y-1.5">
                {selectedTools.map((tool, toolIndex) => (
                  <div
                    key={`${tool}-${toolIndex}`}
                    className="rounded-lg border border-[#d9e6ff] bg-[#f8fbff] px-2 py-1.5 text-[11px] font-semibold leading-5 break-all text-[#1d3f77]"
                    title={tool}
                  >
                    {tool}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="grid gap-2 sm:grid-cols-2">
            <div className="rounded-xl border border-[#d9e6ff] bg-white px-3 py-2">
              <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-[#4f6ea8]">
                Classifier model
              </div>
              <div className="break-all text-[12px] font-medium text-[#27344a]" title={classifierModel || '-'}>
                {classifierModel || '-'}
              </div>
            </div>
            <div className="rounded-xl border border-[#d9e6ff] bg-white px-3 py-2">
              <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-[#4f6ea8]">
                Confidence
              </div>
              <div className="font-mono text-[12px] font-semibold text-[#27344a]">
                {hasConfidence ? formatRouterConfidence(router.confidence) : '-'}
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-[#d9e6ff] bg-white px-3 py-2">
            <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-[#4f6ea8]">
              Reason
            </div>
            <div className="whitespace-pre-wrap text-left text-[12px] leading-5 text-[#475569]">
              {reason || '-'}
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-[#d9e2f2] bg-white px-3 py-2 text-left text-[12px] leading-5 text-[#7b879b]">
          No router data was returned for this user message.
        </div>
      )}
    </div>
  );
}

type DecisionScopeRowProps = {
  label: DecisionMetadataItem['label'];
  value: string;
  copyKey: string;
  copiedMentionKey: string | null;
  onCopy: (value: string, copyKey: string) => void;
};

function DecisionScopeRow({
  label,
  value,
  copyKey,
  copiedMentionKey,
  onCopy
}: DecisionScopeRowProps) {
  const isCopied = copiedMentionKey === copyKey;
  const tooltipLabel = isCopied ? 'Copied' : 'Copy value';

  return (
    <div className="flex items-start justify-between gap-2 rounded-lg border border-[#dbe7ff] bg-[#f8fbff] px-2.5 py-2">
      <div className="min-w-0">
        <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[#4f6ea8]">
          {label}
        </div>
        <div className="mt-0.5 break-all font-mono text-[11px] text-[#5f6f89]" title={value}>
          {formatMentionId(value)}
        </div>
      </div>
      <button
        type="button"
        className="group relative shrink-0 rounded-lg border border-[#d7e3ff] bg-white p-1.5 text-[#4f6ea8] transition-colors hover:border-[#97b9ff] hover:text-[#165BFB]"
        onClick={() => onCopy(value, copyKey)}
        title={tooltipLabel}
        aria-label={`Copy ${label.toLowerCase()} value`}
      >
        {isCopied ? (
          <svg className="size-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 6 9 17l-5-5" />
          </svg>
        ) : (
          <svg className="size-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
          </svg>
        )}
        <span className="pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md bg-[#1f2937] px-2 py-1 text-[10px] font-medium text-white opacity-0 transition-opacity group-hover:opacity-100">
          {tooltipLabel}
        </span>
      </button>
    </div>
  );
}

type DecisionValueRowProps = {
  decisionId?: string;
  details?: DecisionListItemType;
  decisionImagePreview?: DecisionImagePreviewStateType;
  onOpenDecisionImagePreview?: (imageUrl: string, imageName: string) => void;
};

type UpgradeRequiredNoticeProps = {
  showUpgradeButton: boolean;
  isUpgradeButtonLoading?: boolean;
  onUpgradeClick: () => void;
};

function UpgradeRequiredNotice({
  showUpgradeButton,
  isUpgradeButtonLoading = false,
  onUpgradeClick
}: UpgradeRequiredNoticeProps) {
  return (
    <div className="mb-2 rounded-lg border border-[#f4dcb0] bg-[#fff8eb] px-3 py-2.5">
      <div className="text-[14px] font-semibold text-[#1f2a44]">
        {UPGRADE_REQUIRED_TITLE}
      </div>
      <div className="mt-1 text-[12px] leading-5 text-[#475569]">
        {UPGRADE_REQUIRED_DESCRIPTION}
      </div>
      <div className="mt-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-[#b45309]">
        Resolve this step first
      </div>
      {showUpgradeButton && (
        <button
          type="button"
          onClick={onUpgradeClick}
          disabled={isUpgradeButtonLoading}
          className={`agent-upgrade-button mt-2 rounded-full px-3.5 py-1.5 text-[12px] font-semibold text-white transition-opacity ${
            isUpgradeButtonLoading
              ? 'cursor-wait bg-[#f4b8ca]'
              : 'bg-gradient-to-r from-[#eb6f95] to-[#f0b175] hover:opacity-95'
          }`}
        >
          {isUpgradeButtonLoading ? 'Opening...' : 'Upgrade to Pro'}
        </button>
      )}
    </div>
  );
}

function DecisionValueRow({
  decisionId,
  details,
  decisionImagePreview,
  onOpenDecisionImagePreview
}: DecisionValueRowProps) {
  const valueType = details?.valueType;

  if (!details) {
    return (
      <div className="mb-2 rounded-lg border border-[#dbe7ff] bg-[#f8fbff] px-2.5 py-2">
        <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[#4f6ea8]">
          Decision value
        </div>
        <div className="mt-1 text-[12px] text-[#7d8aa3]">
          Loading...
        </div>
      </div>
    );
  }

  const customCodeMode = getCustomCodeDecisionMode(details);
  if (customCodeMode && (valueType === 'shortText' || valueType === 'longText')) {
    const customCodeValue = valueType === 'shortText'
      ? details.value_short_text
      : details.value_long_text;
    const hasCustomCodeValue = typeof customCodeValue === 'string' && customCodeValue.trim().length > 0;

    if (!hasCustomCodeValue) {
      return (
        <div className="mb-2 rounded-lg border border-[#dbe7ff] bg-[#f8fbff] px-2.5 py-2">
          <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[#4f6ea8]">
            Decision value
          </div>
          <div className="mt-1 whitespace-pre-wrap break-words text-[13px] leading-5 text-[#27344a]">
            -
          </div>
        </div>
      );
    }

    const resolvedCustomCodeValue = typeof customCodeValue === 'string' ? customCodeValue : '';

    return (
      <div className="mb-2 rounded-lg border border-[#dbe7ff] bg-[#f8fbff] px-2.5 py-2">
        <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[#4f6ea8]">
          Decision value
        </div>
        <div className="mt-1 custom-code-preview">
          <pre className="custom-code-preview__highlight">
            <code dangerouslySetInnerHTML={{ __html: highlightCustomCodeContent(resolvedCustomCodeValue, customCodeMode) }} />
          </pre>
        </div>
      </div>
    );
  }

  const embedCodeMode = getEmbedCodeDecisionMode(details);
  if (embedCodeMode && (valueType === 'shortText' || valueType === 'longText')) {
    const embedCodeValue = valueType === 'shortText'
      ? details.value_short_text
      : details.value_long_text;
    const hasEmbedCodeValue = typeof embedCodeValue === 'string' && embedCodeValue.trim().length > 0;

    if (!hasEmbedCodeValue) {
      return (
        <div className="mb-2 rounded-lg border border-[#dbe7ff] bg-[#f8fbff] px-2.5 py-2">
          <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[#4f6ea8]">
            Decision value
          </div>
          <div className="mt-1 whitespace-pre-wrap break-words text-[13px] leading-5 text-[#27344a]">
            -
          </div>
        </div>
      );
    }

    const resolvedEmbedCodeValue = typeof embedCodeValue === 'string' ? embedCodeValue : '';

    return (
      <div className="mb-2 rounded-lg border border-[#dbe7ff] bg-[#f8fbff] px-2.5 py-2">
        <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[#4f6ea8]">
          Decision value
        </div>
        <div className="mt-1 custom-code-preview">
          <pre className="custom-code-preview__highlight">
            <code dangerouslySetInnerHTML={{ __html: highlightCustomCodeContent(resolvedEmbedCodeValue, embedCodeMode) }} />
          </pre>
        </div>
      </div>
    );
  }

  if (details.decisionType === 'pageBlockBackgroundImage') {
    const imagePreviewStatus = decisionImagePreview?.status || 'idle';
    const previewUrl = decisionImagePreview?.url;
    const imageTitle = formatDecisionLabel(details);
    const displayValue = valueType === 'longText'
      ? (typeof details.value_long_text === 'string' && details.value_long_text.trim().length > 0
        ? details.value_long_text.trim()
        : '-')
      : (typeof details.value_short_text === 'string' && details.value_short_text.trim().length > 0
        ? details.value_short_text.trim()
        : '-');

    return (
      <div className="mb-2 rounded-lg border border-[#dbe7ff] bg-[#f8fbff] px-2.5 py-2">
        <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[#4f6ea8]">
          Generated image
        </div>
        {imagePreviewStatus === 'ready' && previewUrl ? (
          <button
            type="button"
            className="group mt-2 block rounded-lg border border-[#d9e6ff] bg-white p-1.5 transition-colors hover:border-[#97b9ff]"
            onClick={() => {
              if (onOpenDecisionImagePreview) {
                onOpenDecisionImagePreview(previewUrl, imageTitle);
              }
            }}
            aria-label={`Open generated image preview for decision ${decisionId || ''}`}
          >
            <img
              src={previewUrl}
              alt={imageTitle}
              className="h-24 w-40 rounded-md object-cover transition-opacity group-hover:opacity-95 sm:h-28 sm:w-48"
            />
            <div className="mt-1 text-[10px] font-medium text-[#5f6f89]">
              Click to open full size
            </div>
          </button>
        ) : (
          <div className="mt-1 text-[12px] text-[#7d8aa3]">
            {imagePreviewStatus === 'loading' ? 'Loading image preview...' : 'Preview unavailable'}
          </div>
        )}
        <div className="mt-2 text-[10px] font-semibold uppercase tracking-[0.06em] text-[#4f6ea8]">
          Resource ref
        </div>
        <div className="mt-1 truncate text-[12px] font-mono text-[#27344a]" title={displayValue}>
          {displayValue}
        </div>
      </div>
    );
  }

  if (valueType === 'boolean') {
    const booleanValue = typeof details.value_boolean === 'boolean' ? details.value_boolean : null;
    const displayValue = booleanValue === null ? '-' : booleanValue ? 'Turn on' : 'Turn off';
    const booleanValueClasses = booleanValue === null
      ? 'border-[#d7e3ff] bg-white text-[#5f6f89]'
      : booleanValue
        ? 'border-[#b9e7cd] bg-[#eafaf2] text-[#166534]'
        : 'border-[#f5c2c7] bg-[#fff5f6] text-[#b4232f]';

    return (
      <div className="mb-2 rounded-lg border border-[#dbe7ff] bg-[#f8fbff] px-2.5 py-2">
        <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[#4f6ea8]">
          Decision value
        </div>
        <div className={`mt-1 inline-flex rounded-full border px-2.5 py-1 text-[12px] font-semibold ${booleanValueClasses}`}>
          {displayValue}
        </div>
      </div>
    );
  }

  if (valueType === 'number') {
    const displayValue = typeof details.value_number === 'number' ? details.value_number.toString() : '-';
    return (
      <div className="mb-2 rounded-lg border border-[#dbe7ff] bg-[#f8fbff] px-2.5 py-2">
        <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[#4f6ea8]">
          Decision value
        </div>
        <div className="mt-1 break-all font-mono text-[13px] font-semibold text-[#27344a]">
          {displayValue}
        </div>
      </div>
    );
  }

  if (valueType === 'shortText') {
    const displayValue = typeof details.value_short_text === 'string' && details.value_short_text.trim().length > 0
      ? details.value_short_text.trim()
      : '-';

    return (
      <div className="mb-2 rounded-lg border border-[#dbe7ff] bg-[#f8fbff] px-2.5 py-2">
        <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[#4f6ea8]">
          Decision value
        </div>
        <div className="mt-1 truncate text-[13px] font-semibold text-[#27344a]" title={displayValue}>
          {displayValue}
        </div>
      </div>
    );
  }

  const displayValue = typeof details.value_long_text === 'string' && details.value_long_text.trim().length > 0
    ? details.value_long_text.trim()
    : '-';

  return (
    <div className="mb-2 rounded-lg border border-[#dbe7ff] bg-[#f8fbff] px-2.5 py-2">
      <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[#4f6ea8]">
        Decision value
      </div>
      <div className="mt-1 whitespace-pre-wrap break-words text-[13px] leading-5 text-[#27344a]">
        {displayValue}
      </div>
    </div>
  );
}

type ChatInterfaceProps = {
  currentStep: number;
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
  updateBrief: (field: string, value: string) => void;
  isMobile?: boolean;
  authToken?: string;
  allowedTools?: string[];
  targetSessionId?: string;
  targetMessageId?: string;
  setLastToolsCalls?: (calls: completionFunctionalToolsCallsLogType[]) => void;
  setTotalTokens?: (tokens: number) => void;
  onSessionContextChange?: (context: SessionContextType | null) => void;
  onSessionContextHistoryChange?: (history: SessionContextType[]) => void;
  onSessionSummaryChange?: (summary: SessionSummary | null) => void;
  onSessionRenameOptionsChange?: (renameOptions: SessionRenameOptions | null) => void;
  onSessionUsageChange?: (usage: SessionUsageInfo | null) => void;
};

export default function ChatInterface({
  currentStep,
  setCurrentStep,
  updateBrief,
  isMobile = false,
  authToken,
  allowedTools = [],
  targetSessionId,
  targetMessageId,
  setLastToolsCalls,
  setTotalTokens,
  onSessionContextChange,
  onSessionContextHistoryChange,
  onSessionSummaryChange,
  onSessionRenameOptionsChange,
  onSessionUsageChange
}: ChatInterfaceProps) {
  const { selectedSession, setSelectedSession } = useSession();
  const [messages, setMessages] = useState<Message[]>([chatFlow[0]]);
  const [userInput, setUserInput] = useState('');
  const [chatInputHeight, setChatInputHeight] = useState(CHAT_INPUT_MIN_HEIGHT);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showHelpSuggestions, setShowHelpSuggestions] = useState(false);
  const [sendButtonAnimation, setSendButtonAnimation] = useState(false);
  const [showCompletionButtons, setShowCompletionButtons] = useState(false);
  const [selectedImages, setSelectedImages] = useState<ImageData[]>([]);
  const [previewImage, setPreviewImage] = useState<ImageData | null>(null);
  const [isRequestLoading, setIsLoading] = useState(false);
  const isSiteBrief = selectedSession?.type === 'vibe_site';
  const [siteBrief, setSiteBrief] = useState<SiteBriefChatMetadata | null>(null);
  const [hydratedSessionId, setHydratedSessionId] = useState<string | null>(null);
  const [siteBriefTask, setSiteBriefTask] = useState<SiteBriefTaskType | null>(null);
  const siteBriefTaskRef = useRef(siteBriefTask);
  siteBriefTaskRef.current = siteBriefTask;
  const [siteBriefTaskPollingError, setSiteBriefTaskPollingError] = useState(false);
  const selectedSessionIdRef = useRef(selectedSession?.id);
  const isSiteBriefTaskPending = selectedSession?.type === 'vibe_site'
    && (siteBriefTask?.state === 'queued' || siteBriefTask?.state === 'running');
  const isLoading = isRequestLoading || isSiteBriefTaskPending;

  useLayoutEffect(() => {
    selectedSessionIdRef.current = selectedSession?.id;
    setSiteBriefTask(null);
    setSiteBrief(null);
    setHydratedSessionId(null);
    setIsLoading(false);
    setSelectedImages([]);
    setSiteBriefTaskPollingError(false);
  }, [authToken, selectedSession?.id]);

  useEffect(() => {
    if (selectedSession?.type !== 'vibe_site' || !selectedSession.id || hydratedSessionId !== selectedSession.id) return;
    const sessionId = selectedSession.id;
    const token = authToken || localStorage.getItem('authToken');
    if (!token) return;
    let cancelled = false;
    let inFlight = false;
    let refreshAgain = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let pendingSnapshot: BriefSnapshot | undefined;
    const poll = async (snapshot?: BriefSnapshot) => {
      if (snapshot) {
        pendingSnapshot = snapshot;
      }
      if (cancelled) return;
      if (inFlight) { refreshAgain = true; return; }
      refreshAgain = false;
      inFlight = true;
      clearTimeout(timer);
      let shouldPoll = false;
      try {
        const supplied = pendingSnapshot;
        pendingSnapshot = undefined;
        const currentTask = siteBriefTaskRef.current;
        const result = supplied?.progress ?? await fetchSessionSiteBriefTask(token, sessionId,
          currentTask?.operation === 'conversation' ? currentTask.id : undefined);
        const task = result.task;
        shouldPoll = Boolean(result.site_brief.active_task_id) || task?.state === 'queued' || task?.state === 'running';
        if (!cancelled && selectedSessionIdRef.current === sessionId) {
          setSiteBriefTask(current => {
            if (current && !task && (current.state === 'queued' || current.state === 'running')) return current;
            if (current && task && task.updated_at < current.updated_at) return current;
            return task;
          });
          setSiteBrief(result.site_brief);
          if (result.lastReply) {
            const reply = result.lastReply;
            setMessages(current => current.some(message => message.id === reply.id) ? current : [...current, {
              id: reply.id, type: 'system', role: reply.role, content: reply.content,
              timestamp: reply.timestamp, duration: reply.duration, isLiked: reply.is_liked, isDisliked: reply.is_disliked
            }]);
          }
          setSiteBriefTaskPollingError(false);
        }
      } catch {
        shouldPoll = true;
        if (!cancelled) setSiteBriefTaskPollingError(true);
      } finally {
        inFlight = false;
        if (!cancelled) timer = setTimeout(poll, refreshAgain ? 0 : reconciliationDelay(shouldPoll ? 2000 : 7000));
      }
    };
    const onFocus = () => { void poll(); };
    window.addEventListener('focus', onFocus);
    const unsubscribe = subscribeBrief({ sessionId }, onFocus, snapshot => { void poll(snapshot); });
    void poll();
    return () => { unsubscribe(); cancelled = true; clearTimeout(timer); window.removeEventListener('focus', onFocus); };
  }, [authToken, selectedSession?.id, selectedSession?.type, hydratedSessionId]);
  const [messageActionLoading, setMessageActionLoading] = useState<MessageActionLoadingMap>({});
  const [decisionActionLoading, setDecisionActionLoading] = useState<DecisionActionLoadingMap>({});
  const [decisionDetailsById, setDecisionDetailsById] = useState<Record<string, DecisionListItemType>>({});
  const [decisionImagePreviewById, setDecisionImagePreviewById] = useState<Record<string, DecisionImagePreviewStateType>>({});
  const [decisionUserInputModal, setDecisionUserInputModal] = useState<DecisionUserInputModalState | null>(null);
  const [upgradeRequiredModal, setUpgradeRequiredModal] = useState<UpgradeRequiredModalState | null>(null);
  const [upgradeRequiredOpenLoadingByDecisionId, setUpgradeRequiredOpenLoadingByDecisionId] = useState<Record<string, boolean>>({});
  const [offerPlanUpgradeModal, setOfferPlanUpgradeModal] = useState<OfferPlanUpgradeModalState | null>(null);
  const [offerPlanUpgradeOpenLoadingByMessageId, setOfferPlanUpgradeOpenLoadingByMessageId] = useState<Record<string, boolean>>({});
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const [copiedMentionKey, setCopiedMentionKey] = useState<string | null>(null);
  const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(null);
  const [typingState, setTypingState] = useState<{ id: string; content: string } | null>(null);
  const [typingMessageId, setTypingMessageId] = useState<string | null>(null);
  const [selectedMentionWebsite, setSelectedMentionWebsite] = useState<WebliumWebsiteMentionItem | null>(null);
  const [selectedMentionPages, setSelectedMentionPages] = useState<MentionItemType[]>([]);
  const [selectedMentionBlock, setSelectedMentionBlock] = useState<MentionItemType | null>(null);
  const [selectedMentionElements, setSelectedMentionElements] = useState<MentionItemType[]>([]);
  const [mentionWebsites, setMentionWebsites] = useState<WebliumWebsiteMentionItem[]>([]);
  const [mentionPages, setMentionPages] = useState<WebliumWebsitePageMentionItem[]>([]);
  const [mentionBlocks, setMentionBlocks] = useState<WebliumWebsiteBlockMentionItem[]>([]);
  const [mentionElements, setMentionElements] = useState<WebliumWebsiteElementMentionItem[]>([]);
  const [isMentionPickerOpen, setIsMentionPickerOpen] = useState(false);
  const [isMentionPickerLoading, setIsMentionPickerLoading] = useState(false);
  const [mentionPickerError, setMentionPickerError] = useState<string | null>(null);
  const [isPageMentionPickerOpen, setIsPageMentionPickerOpen] = useState(false);
  const [isPageMentionPickerLoading, setIsPageMentionPickerLoading] = useState(false);
  const [pageMentionPickerError, setPageMentionPickerError] = useState<string | null>(null);
  const [pageMentionSelectionError, setPageMentionSelectionError] = useState<string | null>(null);
  const [isBlockMentionPickerOpen, setIsBlockMentionPickerOpen] = useState(false);
  const [isBlockMentionPickerLoading, setIsBlockMentionPickerLoading] = useState(false);
  const [blockMentionPickerError, setBlockMentionPickerError] = useState<string | null>(null);
  const [isElementMentionPickerOpen, setIsElementMentionPickerOpen] = useState(false);
  const [isElementMentionPickerLoading, setIsElementMentionPickerLoading] = useState(false);
  const [elementMentionPickerError, setElementMentionPickerError] = useState<string | null>(null);
  const [elementMentionSelectionError, setElementMentionSelectionError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const chatContainerRef = useRef<HTMLDivElement | null>(null);
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const mentionControlsRef = useRef<HTMLDivElement | null>(null);
  const typedMessageIdsRef = useRef<Set<string>>(new Set());
  const decisionImagePreviewByIdRef = useRef<Record<string, DecisionImagePreviewStateType>>({});
  const decisionImageFetchInFlightRef = useRef<Set<string>>(new Set());
  const decisionImageFetchGenerationRef = useRef(0);
  const isHydratingSessionRef = useRef(false);
  const handledDeepLinkRef = useRef<string | null>(null);
  const { selectedLanguage, setSelectedLanguage } = useLanguage();
  const vibeBlockDesignWizardProgress = useMemo(
    () => buildVibeBlockDesignWizardProgress(messages),
    [messages]
  );
  const latestVibeBlockDesignWizardProgress = vibeBlockDesignWizardProgress[vibeBlockDesignWizardProgress.length - 1] || null;
  const activeVibeBlockDesignWizardProgress = latestVibeBlockDesignWizardProgress?.isComplete
    || latestVibeBlockDesignWizardProgress?.isSkipped
    ? null
    : latestVibeBlockDesignWizardProgress;
  const pendingVibeBlockDesignWizard = activeVibeBlockDesignWizardProgress?.latestStep.answer
    ? null
    : activeVibeBlockDesignWizardProgress?.latestStep.wizard || null;
  const isVibeBlockDesignWizardAdvancing = Boolean(
    activeVibeBlockDesignWizardProgress?.latestStep.answer && isLoading
  );
  const visibleVibeBlockDesignWizardProgress = pendingVibeBlockDesignWizard || isVibeBlockDesignWizardAdvancing
    ? activeVibeBlockDesignWizardProgress
    : null;
  const isVibeBlockDesignWizardPending = Boolean(visibleVibeBlockDesignWizardProgress);

  useEffect(() => {
    if (!isVibeBlockDesignWizardPending) return;

    setIsMentionPickerOpen(false);
    setIsPageMentionPickerOpen(false);
    setIsBlockMentionPickerOpen(false);
    setIsElementMentionPickerOpen(false);
  }, [isVibeBlockDesignWizardPending]);

  const updateChatInputHeight = useCallback(() => {
    const textarea = inputRef.current;

    if (!textarea) {
      return;
    }

    textarea.style.height = '0px';

    const nextHeight = getSteppedChatInputHeight(textarea.scrollHeight);
    textarea.style.height = `${nextHeight}px`;

    setChatInputHeight((currentHeight) => currentHeight === nextHeight ? currentHeight : nextHeight);
  }, []);

  const updateActionLoading = useCallback((messageId: string, action: MessageActionKey, value: boolean) => {
    setMessageActionLoading((prev) => {
      const current = prev[messageId] || { like: false, dislike: false, delete: false };
      return {
        ...prev,
        [messageId]: {
          ...current,
          [action]: value
        }
      };
    });
  }, []);

  const updateDecisionActionLoading = useCallback((decisionId: string, action: DecisionActionKey, value: boolean) => {
    setDecisionActionLoading((prev) => {
      const current = prev[decisionId] || { accept: false, decline: false, userInput: false, publish: false };
      return {
        ...prev,
        [decisionId]: {
          ...current,
          [action]: value
        }
      };
    });
  }, []);

  const clearDecisionImagePreviews = useCallback(() => {
    decisionImageFetchGenerationRef.current += 1;
    decisionImageFetchInFlightRef.current.clear();
    setDecisionImagePreviewById((prev) => {
      Object.values(prev).forEach((preview) => {
        if (preview?.url) {
          URL.revokeObjectURL(preview.url);
        }
      });
      return {};
    });
  }, []);

  const getWebsiteNameById = useCallback((websiteId: string): string | null => {
    const website = mentionWebsites.find((item) => item.id === websiteId);
    if (!website) {
      return null;
    }
    return website.name || getWebsiteDomainLabel(website);
  }, [mentionWebsites]);

  const getWebsiteMentionLabel = useCallback((mentions?: MessageMentionsType, fallbackName?: string): string | null => {
    const websiteId = getWebsiteMentionId(mentions);
    if (!websiteId) {
      return null;
    }

    const mentionName = mentions?.website?.name;
    if (typeof mentionName === 'string' && mentionName.trim().length > 0) {
      return mentionName;
    }

    if (fallbackName && fallbackName.trim().length > 0) {
      return fallbackName;
    }

    const websiteName = getWebsiteNameById(websiteId);
    if (websiteName) {
      return websiteName;
    }

    return formatWebsiteMentionFallback(websiteId);
  }, [getWebsiteNameById]);

  const getPageMentionLabel = useCallback((pageMention: MentionItemType): string => {
    const mentionLabel = typeof pageMention.label === 'string' ? pageMention.label.trim() : '';
    if (mentionLabel.length > 0) {
      return mentionLabel;
    }

    const mentionName = typeof pageMention.name === 'string' ? pageMention.name.trim() : '';
    if (mentionName.length > 0) {
      return mentionName;
    }

    return formatPageMentionFallback(pageMention.id);
  }, []);

  const getBlockMentionLabel = useCallback((blockMention: MentionItemType): string => {
    const mentionLabel = typeof blockMention.label === 'string' ? blockMention.label.trim() : '';
    if (mentionLabel.length > 0) {
      return mentionLabel;
    }

    const mentionName = typeof blockMention.name === 'string' ? blockMention.name.trim() : '';
    if (mentionName.length > 0) {
      return mentionName;
    }

    return formatBlockMentionFallback(blockMention.id);
  }, []);

  const getElementMentionLabel = useCallback((elementMention: MentionItemType): string => {
    const mentionLabel = typeof elementMention.label === 'string' ? elementMention.label.trim() : '';
    if (mentionLabel.length > 0) {
      return mentionLabel;
    }

    const mentionName = typeof elementMention.name === 'string' ? elementMention.name.trim() : '';
    if (mentionName.length > 0) {
      return mentionName;
    }

    return formatElementMentionFallback(elementMention.id);
  }, []);

  //const isChatFinished = currentStep >= chatFlow.length - 1;
  //const isChatFinished = currentStep >= 20 || chatFlow[currentStep].isFinished || false; // never finish the chat
  const isChatFinished = isSiteBrief && (!siteBrief || siteBrief.status !== 'gather_requirements' || Boolean(siteBrief.active_task_id));
  //console.log("isChatFinished", isChatFinished, currentStep, chatFlow.length);

  // Create chat actions object for the business logic functions
  const chatActions: ChatActions = {
    setMessages,
    getMessages: () => messages,
    setUserInput,
    setCurrentStep,
    updateBrief,
    setSendButtonAnimation,
    setShowHelpSuggestions,
    setShowCompletionButtons,
    setIsLoading,
    focusInput: () => inputRef.current?.focus(),
    getAuthToken: () => authToken || localStorage.getItem('authToken'),
    getAllowedTools: () => allowedTools,
    getSessionId: () => selectedSessionIdRef.current,
    getSessionType: () => selectedSession?.type,
    getSiteBrief: () => siteBrief,
    setSiteBrief: (brief, sessionId) => {
      if (selectedSessionIdRef.current === sessionId) setSiteBrief(brief);
    },
    getLanguage: () => selectedLanguage,
    setSessionFromResponse: (session) => setSelectedSession(session),
    setLastToolsCalls: (calls) => {
      if (setLastToolsCalls) {
        setLastToolsCalls(calls as completionFunctionalToolsCallsLogType[]);
      }
    },
    setTotalTokens: (t: number) => { setTotalTokens && setTotalTokens(t); },
    setSiteBriefTask: (task, sessionId) => {
      if (selectedSessionIdRef.current !== sessionId) return;
      setSiteBriefTask(current => current && task && task.updated_at < current.updated_at ? current : task ?? null);
    },
    getSelectedMentionWebsite: () => selectedMentionWebsite
      ? {
        id: selectedMentionWebsite.id,
        name: selectedMentionWebsite.name || getWebsiteDomainLabel(selectedMentionWebsite),
        status: selectedMentionWebsite.status,
        domainLabel: selectedMentionWebsite.domainLabel
      }
      : null,
    getSelectedMentionPages: () => selectedMentionPages,
    getSelectedMentionBlock: () => selectedMentionBlock,
    getSelectedMentionElements: () => selectedMentionElements
  };

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const frameId = window.requestAnimationFrame(updateChatInputHeight);
    return () => window.cancelAnimationFrame(frameId);
  }, [userInput, updateChatInputHeight]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleResize = () => {
      updateChatInputHeight();
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [updateChatInputHeight]);

  // Add the next message when currentStep changes
  useEffect(() => {
    if (currentStep < chatFlow.length) {
      const currentFlowMessage = chatFlow[currentStep];
      setShowSuggestions(Boolean(currentFlowMessage?.suggestions && currentFlowMessage.suggestions.length > 0));
    } else {
      setShowSuggestions(false);
    }
    setShowHelpSuggestions(false);
  }, [currentStep]);

  useEffect(() => {
    if (!copiedMessageId) {
      return;
    }

    const timeout = setTimeout(() => setCopiedMessageId(null), 2000);
    return () => clearTimeout(timeout);
  }, [copiedMessageId]);

  useEffect(() => {
    if (!copiedMentionKey) {
      return;
    }

    const timeout = setTimeout(() => setCopiedMentionKey(null), 1800);
    return () => clearTimeout(timeout);
  }, [copiedMentionKey]);

  useEffect(() => {
    if (!previewImage) {
      return;
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setPreviewImage(null);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [previewImage]);

  useEffect(() => {
    return () => {
      clearDecisionImagePreviews();
    };
  }, [clearDecisionImagePreviews]);

  useEffect(() => {
    decisionImagePreviewByIdRef.current = decisionImagePreviewById;
  }, [decisionImagePreviewById]);

  // Hydrate chat when a session is selected
  useEffect(() => {
    let cancelled = false;
    const loadSelectedSession = async () => {
      if (!selectedSession?.id) {
        setSelectedMentionWebsite(null);
        setSelectedMentionPages([]);
        setSelectedMentionBlock(null);
        setSelectedMentionElements([]);
        setMentionPages([]);
        setMentionBlocks([]);
        setMentionElements([]);
        setDecisionActionLoading({});
        setDecisionDetailsById({});
        setOfferPlanUpgradeModal(null);
        setOfferPlanUpgradeOpenLoadingByMessageId({});
        clearDecisionImagePreviews();
        setIsPageMentionPickerOpen(false);
        setIsBlockMentionPickerOpen(false);
        setIsElementMentionPickerOpen(false);
        setPageMentionPickerError(null);
        setPageMentionSelectionError(null);
        setBlockMentionPickerError(null);
        setElementMentionPickerError(null);
        setElementMentionSelectionError(null);
        onSessionContextChange && onSessionContextChange(null);
        onSessionContextHistoryChange && onSessionContextHistoryChange([]);
        onSessionSummaryChange && onSessionSummaryChange(null);
        onSessionRenameOptionsChange && onSessionRenameOptionsChange(null);
        onSessionUsageChange && onSessionUsageChange(null);
        return;
      }
      setSelectedMentionWebsite(null);
      setSelectedMentionPages([]);
      setSelectedMentionBlock(null);
      setSelectedMentionElements([]);
      setMentionPages([]);
      setMentionBlocks([]);
      setMentionElements([]);
      setDecisionActionLoading({});
      setDecisionDetailsById({});
      setOfferPlanUpgradeModal(null);
      setOfferPlanUpgradeOpenLoadingByMessageId({});
      clearDecisionImagePreviews();
      setIsPageMentionPickerOpen(false);
      setIsBlockMentionPickerOpen(false);
      setIsElementMentionPickerOpen(false);
      setPageMentionPickerError(null);
      setPageMentionSelectionError(null);
      setBlockMentionPickerError(null);
      setElementMentionPickerError(null);
      setElementMentionSelectionError(null);
      const token = localStorage.getItem('authToken');
      if (!token) return;
      try {
        const data = await fetchSessionById(token, selectedSession.id, selectedLanguage);
        if (cancelled) return;
        try {
          const loadedDecisions = await fetchDecisionsBySessionId(token, selectedSession.id);
          if (cancelled) return;
          setDecisionDetailsById(
            loadedDecisions.reduce<Record<string, DecisionListItemType>>((acc, decision) => {
              acc[decision.id] = decision;
              return acc;
            }, {})
          );
        } catch (decisionLoadError) {
          console.error('Failed to fetch decisions for selected session', decisionLoadError);
        }

        if (cancelled) return;
        setSiteBrief(data.session.site_brief ?? null);
        setHydratedSessionId(data.session.id);

        // Set token usage from the loaded session
        setTotalTokens && setTotalTokens(data.session.totalTokens);
        setLastToolsCalls && setLastToolsCalls(data.lastToolsCalls as completionFunctionalToolsCallsLogType[]);
        setSelectedSession({
          id: data.session.id,
          name: data.session.name,
          type: data.session.type || 'general',
          vibe_block: data.session.vibe_block || null,
        });
        onSessionContextChange && onSessionContextChange(data.session.current_context ? {
          websiteId: data.session.current_context.websiteId ?? null,
          websiteName: data.session.current_context.websiteName ?? null,
          pageName: data.session.current_context.pageName ?? null
        } : null);
        onSessionContextHistoryChange && onSessionContextHistoryChange(
          (data.session.context_history || []).map((entry) => ({
            websiteId: entry.websiteId ?? null,
            websiteName: entry.websiteName ?? null,
            pageName: entry.pageName ?? null
          }))
        );
        onSessionSummaryChange && onSessionSummaryChange(data.session.summary ?? null);
        onSessionRenameOptionsChange && onSessionRenameOptionsChange(data.session.renameOptions ?? null);
        onSessionUsageChange && onSessionUsageChange(data.session.usage ?? null);

        // Map backend messages to UI messages
        const mapped = data.session.messages.map((m) => {
          const isUser = m.role === 'user';

          // Map images from backend format to frontend ImageData format
          const images: ImageData[] | undefined = m.images && m.images.length > 0
            ? m.images.map((img, idx) => ({
                url: img.url,
                name: img.name || `Image ${idx + 1}`,
                size: '' // Size not stored in backend
              }))
            : undefined;

          return {
            id: m.id,
            type: isUser ? 'user' : 'system',
            role: m.role,
            content: String(m.content ?? ''),
            mentions: m.mentions,
            router: m.router,
            decisions: m.decisions,
            offer_plan_upgrade: m.offer_plan_upgrade,
            vibe_block_design_wizard_question: m.vibe_block_design_wizard_question,
            vibe_block_design_wizard_answer: m.vibe_block_design_wizard_answer,
            vibeBlockDesignWizardAnswerStatus: m.vibe_block_design_wizard_answer ? 'confirmed' : undefined,
            images, // Include images for user messages
            timestamp: m.timestamp,
            duration: m.duration,
            isLiked: m.is_liked,
            isDisliked: m.is_disliked
          } as Message;
        });

        // Replace messages with those from the selected session only
        isHydratingSessionRef.current = true;
        setMessages(mapped);
        typedMessageIdsRef.current = new Set(mapped.map((msg) => String(msg.id)));

        const sessionLanguage = data.session.language;
        if (sessionLanguage && isSupportedLanguage(sessionLanguage)) {
          setSelectedLanguage(sessionLanguage);
        }

        // Reset current step within chatFlow bounds so effect that appends AI message runs
        // We keep currentStep aligned to chatFlow, not to the number of historic messages
        setCurrentStep(Math.max(0, Math.min(chatFlow.length - 1, mapped.length)));
        setTimeout(() => {
          isHydratingSessionRef.current = false;
        }, 0);
      } catch (e) {
        if (cancelled) return;
        console.error('Failed to load session', e);
        onSessionContextChange && onSessionContextChange(null);
        onSessionContextHistoryChange && onSessionContextHistoryChange([]);
        onSessionUsageChange && onSessionUsageChange(null);
      }
    };
    loadSelectedSession();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSession?.id]);

  useEffect(() => {
    if (messages.length === 0) {
      return;
    }

    if (typedMessageIdsRef.current.size === 0) {
      typedMessageIdsRef.current = new Set(messages.map((msg) => String(msg.id)));
    }
  }, [messages]);

  useEffect(() => {
    if (!messages.length || isHydratingSessionRef.current) {
      return;
    }

    const lastMessage = messages[messages.length - 1];
    const lastMessageId = String(lastMessage.id);

    if (lastMessage.type !== 'system') {
      return;
    }

    if (typedMessageIdsRef.current.has(lastMessageId)) {
      return;
    }

    setTypingMessageId(lastMessageId);
    setTypingState({ id: lastMessageId, content: '' });
  }, [messages]);

  useEffect(() => {
    const token = authToken || localStorage.getItem('authToken');
    if (!token) {
      return;
    }

    if (!selectedSession?.id) {
      return;
    }

    const missingDecisionIds = Array.from(new Set(
      messages
        .flatMap((message) => (message.decisions || []).map((decision) => decision.id))
        .filter((decisionId) => decisionId && !decisionDetailsById[decisionId])
    ));

    if (missingDecisionIds.length === 0) {
      return;
    }

    fetchDecisionsBySessionId(token, selectedSession.id)
      .then((loadedDecisions) => {
        if (!loadedDecisions || loadedDecisions.length === 0) {
          return;
        }

        setDecisionDetailsById((prev) => {
          const next = { ...prev };
          for (const decision of loadedDecisions) {
            next[decision.id] = decision;
          }
          return next;
        });
      })
      .catch((error) => {
        console.error('Failed to hydrate decision details', error);
      });
  }, [authToken, decisionDetailsById, messages, selectedSession?.id]);

  useEffect(() => {
    const token = authToken || localStorage.getItem('authToken');
    if (!token || !selectedSession?.id) {
      return;
    }

    const backgroundDecisionIds = Array.from(new Set(
      messages
        .flatMap((message) => (message.decisions || []).map((decision) => decision.id))
        .filter((decisionId) => {
          if (!decisionId) {
            return false;
          }

          const details = decisionDetailsById[decisionId];
          return details?.decisionType === 'pageBlockBackgroundImage';
        })
    ));

    if (backgroundDecisionIds.length === 0) {
      return;
    }

    const requestGeneration = decisionImageFetchGenerationRef.current;

    backgroundDecisionIds.forEach((decisionId) => {
      const currentPreview = decisionImagePreviewByIdRef.current[decisionId];

      if (currentPreview?.status === 'ready' || currentPreview?.status === 'loading') {
        return;
      }

      if (decisionImageFetchInFlightRef.current.has(decisionId)) {
        return;
      }

      decisionImageFetchInFlightRef.current.add(decisionId);
      setDecisionImagePreviewById((prev) => ({
        ...prev,
        [decisionId]: {
          status: 'loading'
        }
      }));

      fetchDecisionGeneratedImageBlob(token, selectedSession.id, decisionId)
        .then((imageBlob) => {
          const imageUrl = URL.createObjectURL(imageBlob);

          if (decisionImageFetchGenerationRef.current !== requestGeneration) {
            URL.revokeObjectURL(imageUrl);
            return;
          }

          setDecisionImagePreviewById((prev) => {
            const currentUrl = prev[decisionId]?.url;
            if (currentUrl) {
              URL.revokeObjectURL(currentUrl);
            }

            return {
              ...prev,
              [decisionId]: {
                status: 'ready',
                url: imageUrl
              }
            };
          });
        })
        .catch((error) => {
          if (decisionImageFetchGenerationRef.current !== requestGeneration) {
            return;
          }

          console.error(`Failed to fetch generated image for decision ${decisionId}`, error);
          setDecisionImagePreviewById((prev) => ({
            ...prev,
            [decisionId]: {
              status: 'error'
            }
          }));
        })
        .finally(() => {
          decisionImageFetchInFlightRef.current.delete(decisionId);
        });
    });
  }, [authToken, decisionDetailsById, messages, selectedSession?.id]);

  useEffect(() => {
    if (!typingMessageId) {
      return;
    }

    const activeMessage = messages.find((msg) => String(msg.id) === typingMessageId);
    if (!activeMessage || typeof activeMessage.content !== 'string') {
      setTypingMessageId(null);
      setTypingState(null);
      return;
    }

    const fullText = activeMessage.content;
    if (!fullText.length) {
      typedMessageIdsRef.current.add(typingMessageId);
      setTypingMessageId(null);
      setTypingState(null);
      return;
    }

    let index = 0;
    const maxDelay = 30;
    const minDelay = 10;
    const dynamicDelay = Math.floor(800 / Math.max(20, fullText.length));
    const intervalDelay = Math.min(maxDelay, Math.max(minDelay, dynamicDelay));

    const interval = setInterval(() => {
      index = Math.min(fullText.length, index + 2);
      setTypingState({ id: typingMessageId, content: fullText.slice(0, index) });

      if (index >= fullText.length) {
        clearInterval(interval);
        typedMessageIdsRef.current.add(typingMessageId);
        setTypingMessageId(null);
        setTypingState(null);
      }
    }, intervalDelay);

    return () => clearInterval(interval);
  }, [messages, typingMessageId]);

  // Scroll to bottom of messages with a buffer space above the input
  useEffect(() => {
    const deepLinkKey = targetSessionId && targetMessageId
      ? `${targetSessionId}:${targetMessageId}`
      : null;

    if (
      deepLinkKey
      && targetSessionId === selectedSession?.id
      && handledDeepLinkRef.current !== deepLinkKey
    ) {
      return;
    }

    if (messagesEndRef.current) {
      const scrollContainer = chatContainerRef.current;
      if (scrollContainer) {
        // Add a delay to ensure animations have completed
        setTimeout(() => {
          messagesEndRef.current && messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      }
    }
  }, [messages, selectedImages, selectedSession?.id, targetMessageId, targetSessionId]);

  useEffect(() => {
    if (
      !targetMessageId
      || !targetSessionId
      || selectedSession?.id !== targetSessionId
      || !chatContainerRef.current
    ) {
      setHighlightedMessageId(null);
      return undefined;
    }

    const deepLinkKey = `${targetSessionId}:${targetMessageId}`;
    if (handledDeepLinkRef.current === deepLinkKey) return undefined;

    const targetElement = Array.from(
      chatContainerRef.current.querySelectorAll<HTMLElement>('[data-message-id]')
    ).find(element => element.dataset.messageId === targetMessageId);

    if (!targetElement) return undefined;
    handledDeepLinkRef.current = deepLinkKey;

    const scrollTimeout = window.setTimeout(() => {
      setHighlightedMessageId(targetMessageId);
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 150);
    const highlightTimeout = window.setTimeout(() => {
      setHighlightedMessageId(current => current === targetMessageId ? null : current);
    }, 2800);

    return () => {
      window.clearTimeout(scrollTimeout);
      window.clearTimeout(highlightTimeout);
    };
  }, [messages, selectedSession?.id, targetMessageId, targetSessionId]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        (isMentionPickerOpen || isPageMentionPickerOpen || isBlockMentionPickerOpen || isElementMentionPickerOpen) &&
        mentionControlsRef.current &&
        event.target instanceof Node &&
        !mentionControlsRef.current.contains(event.target)
      ) {
        setIsMentionPickerOpen(false);
        setIsPageMentionPickerOpen(false);
        setIsBlockMentionPickerOpen(false);
        setIsElementMentionPickerOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMentionPickerOpen, isPageMentionPickerOpen, isBlockMentionPickerOpen, isElementMentionPickerOpen]);

  const clearMentionElementSelection = useCallback(() => {
    setSelectedMentionElements([]);
    setMentionElements([]);
    setIsElementMentionPickerOpen(false);
    setElementMentionPickerError(null);
    setElementMentionSelectionError(null);
  }, []);

  const clearMentionBlockSelection = useCallback(() => {
    setSelectedMentionBlock(null);
    setMentionBlocks([]);
    setIsBlockMentionPickerOpen(false);
    setBlockMentionPickerError(null);
    clearMentionElementSelection();
  }, [clearMentionElementSelection]);

  const loadWebliumWebsitesForPickers = async (): Promise<WebliumWebsiteMentionItem[]> => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      throw new Error('Authorization token is missing.');
    }

    const websites = await fetchWebliumWebsites(token);
    setMentionWebsites(websites);
    return websites;
  };

  const openMentionPicker = async () => {
    setIsMentionPickerOpen(true);
    setIsMentionPickerLoading(true);
    setMentionPickerError(null);

    try {
      await loadWebliumWebsitesForPickers();
    } catch (error) {
      console.error('Failed to fetch mention websites', error);
      setMentionPickerError(error instanceof Error ? error.message : 'Failed to load websites');
    } finally {
      setIsMentionPickerLoading(false);
    }
  };

  const handleMentionPickerToggle = () => {
    if (isMentionPickerOpen) {
      setIsMentionPickerOpen(false);
      return;
    }

    setIsPageMentionPickerOpen(false);
    setIsBlockMentionPickerOpen(false);
    setIsElementMentionPickerOpen(false);
    openMentionPicker().catch((error) => {
      console.error('Failed to open mention picker', error);
    });
  };

  const handleSelectMentionWebsite = (website: WebliumWebsiteMentionItem) => {
    const isWebsiteChanged = selectedMentionWebsite?.id !== website.id;
    setSelectedMentionWebsite(website);
    setPageMentionPickerError(null);
    if (isWebsiteChanged) {
      setSelectedMentionPages([]);
      setMentionPages([]);
      setPageMentionSelectionError(null);
      clearMentionBlockSelection();
    }
    setIsMentionPickerOpen(false);
    setIsPageMentionPickerOpen(false);
    setIsBlockMentionPickerOpen(false);
    setIsElementMentionPickerOpen(false);
  };

  const handleClearMentionWebsite = () => {
    setSelectedMentionWebsite(null);
    setSelectedMentionPages([]);
    setMentionPages([]);
    setIsMentionPickerOpen(false);
    setIsPageMentionPickerOpen(false);
    setPageMentionPickerError(null);
    setPageMentionSelectionError(null);
    clearMentionBlockSelection();
  };

  const openPageMentionPicker = async () => {
    if (!selectedMentionWebsite) {
      return;
    }

    const token = localStorage.getItem('authToken');
    if (!token) {
      setPageMentionPickerError('Authorization token is missing.');
      setIsPageMentionPickerOpen(true);
      return;
    }

    setIsPageMentionPickerOpen(true);
    setIsPageMentionPickerLoading(true);
    setPageMentionPickerError(null);
    setPageMentionSelectionError(null);

    try {
      const pages = await fetchWebliumWebsitePages(token, selectedMentionWebsite.id);
      setMentionPages(pages);
    } catch (error) {
      console.error('Failed to fetch mention pages', error);
      setPageMentionPickerError(error instanceof Error ? error.message : 'Failed to load pages');
    } finally {
      setIsPageMentionPickerLoading(false);
    }
  };

  const handlePageMentionPickerToggle = () => {
    if (!selectedMentionWebsite) {
      return;
    }

    if (isPageMentionPickerOpen) {
      setIsPageMentionPickerOpen(false);
      return;
    }

    setIsMentionPickerOpen(false);
    setIsBlockMentionPickerOpen(false);
    setIsElementMentionPickerOpen(false);
    openPageMentionPicker().catch((error) => {
      console.error('Failed to open page mention picker', error);
    });
  };

  const handleToggleMentionPage = (page: WebliumWebsitePageMentionItem) => {
    clearMentionBlockSelection();
    setSelectedMentionPages((prev) => {
      const isAlreadySelected = prev.some((selectedPage) => selectedPage.id === page.id);
      if (isAlreadySelected) {
        setPageMentionSelectionError(null);
        return prev.filter((selectedPage) => selectedPage.id !== page.id);
      }

      if (prev.length >= 10) {
        setPageMentionSelectionError('You can mention up to 10 pages.');
        return prev;
      }

      setPageMentionSelectionError(null);
      return [
        ...prev,
        {
          id: page.id,
          name: page.name,
          label: page.label,
          lang: page.lang,
          lang_group: page.lang_group,
          is_home_page: page.is_home_page
        }
      ];
    });
  };

  const handleRemoveMentionPage = (pageId: string) => {
    clearMentionBlockSelection();
    setSelectedMentionPages((prev) => prev.filter((page) => page.id !== pageId));
    setPageMentionSelectionError(null);
  };

  const openBlockMentionPicker = async () => {
    if (!selectedMentionWebsite || selectedMentionPages.length === 0) {
      return;
    }

    const pageId = selectedMentionPages[0]?.id;
    if (!pageId) {
      return;
    }

    const token = localStorage.getItem('authToken');
    if (!token) {
      setBlockMentionPickerError('Authorization token is missing.');
      setIsBlockMentionPickerOpen(true);
      return;
    }

    setIsBlockMentionPickerOpen(true);
    setIsBlockMentionPickerLoading(true);
    setBlockMentionPickerError(null);

    try {
      const blocks = await fetchWebliumWebsiteBlocks(token, selectedMentionWebsite.id, pageId);
      setMentionBlocks(blocks);
    } catch (error) {
      console.error('Failed to fetch mention blocks', error);
      setBlockMentionPickerError(error instanceof Error ? error.message : 'Failed to load blocks');
    } finally {
      setIsBlockMentionPickerLoading(false);
    }
  };

  const handleBlockMentionPickerToggle = () => {
    if (!selectedMentionWebsite || selectedMentionPages.length === 0) {
      return;
    }

    if (isBlockMentionPickerOpen) {
      setIsBlockMentionPickerOpen(false);
      return;
    }

    setIsMentionPickerOpen(false);
    setIsPageMentionPickerOpen(false);
    setIsElementMentionPickerOpen(false);
    openBlockMentionPicker().catch((error) => {
      console.error('Failed to open block mention picker', error);
    });
  };

  const handleSelectMentionBlock = (block: WebliumWebsiteBlockMentionItem) => {
    clearMentionElementSelection();
    setSelectedMentionBlock({
      id: block.id,
      name: block.name || block.label || '',
      label: block.label
    });
    setIsBlockMentionPickerOpen(false);
    setBlockMentionPickerError(null);
  };

  const handleClearMentionBlock = () => {
    clearMentionBlockSelection();
  };

  const openElementMentionPicker = async () => {
    if (!selectedMentionWebsite || selectedMentionPages.length === 0 || !selectedMentionBlock) {
      return;
    }

    const pageId = selectedMentionPages[0]?.id;
    if (!pageId) {
      return;
    }

    const token = localStorage.getItem('authToken');
    if (!token) {
      setElementMentionPickerError('Authorization token is missing.');
      setIsElementMentionPickerOpen(true);
      return;
    }

    setIsElementMentionPickerOpen(true);
    setIsElementMentionPickerLoading(true);
    setElementMentionPickerError(null);
    setElementMentionSelectionError(null);

    try {
      const elements = await fetchWebliumWebsiteBlockElements(
        token,
        selectedMentionWebsite.id,
        pageId,
        selectedMentionBlock.id
      );
      setMentionElements(elements);
    } catch (error) {
      console.error('Failed to fetch mention elements', error);
      setElementMentionPickerError(error instanceof Error ? error.message : 'Failed to load elements');
    } finally {
      setIsElementMentionPickerLoading(false);
    }
  };

  const handleElementMentionPickerToggle = () => {
    if (!selectedMentionWebsite || selectedMentionPages.length === 0 || !selectedMentionBlock) {
      return;
    }

    if (isElementMentionPickerOpen) {
      setIsElementMentionPickerOpen(false);
      return;
    }

    setIsMentionPickerOpen(false);
    setIsPageMentionPickerOpen(false);
    setIsBlockMentionPickerOpen(false);
    openElementMentionPicker().catch((error) => {
      console.error('Failed to open element mention picker', error);
    });
  };

  const handleToggleMentionElement = (element: WebliumWebsiteElementMentionItem) => {
    setSelectedMentionElements((prev) => {
      const isAlreadySelected = prev.some((selectedElement) => selectedElement.id === element.id);
      if (isAlreadySelected) {
        setElementMentionSelectionError(null);
        return prev.filter((selectedElement) => selectedElement.id !== element.id);
      }

      if (prev.length >= 10) {
        setElementMentionSelectionError('You can mention up to 10 elements.');
        return prev;
      }

      setElementMentionSelectionError(null);
      return [
        ...prev,
        {
          id: element.id,
          name: element.name || element.label || '',
          type: element.type,
          label: element.label
        }
      ];
    });
    setElementMentionPickerError(null);
  };

  const handleRemoveMentionElement = (elementId: string) => {
    setSelectedMentionElements((prev) => prev.filter((element) => element.id !== elementId));
    setElementMentionSelectionError(null);
  };

  const handleUserResponse = async (
    response: string | null = null,
    vibeBlockDesignWizardAnswer?: VibeBlockDesignWizardAnswerType
  ) => {
    if (isLoading || isChatFinished) return;
    if (isVibeBlockDesignWizardPending && !vibeBlockDesignWizardAnswer) return;

    const isWizardAnswer = Boolean(vibeBlockDesignWizardAnswer);
    const imagesToSend = isWizardAnswer ? [] : [...selectedImages];
    if (!isWizardAnswer) {
      setSelectedImages([]); // Clear images after sending
    }
    await chatHandleUserResponse(
      chatActions,
      currentStep,
      userInput,
      response,
      imagesToSend,
      vibeBlockDesignWizardAnswer,
      isWizardAnswer
        ? { clearUserInput: false, includeMentions: false }
        : undefined
    );
  };

  const handleVibeBlockDesignWizardAnswer = async (answer: VibeBlockDesignWizardAnswerType) => {
    if (
      isLoading
      || !pendingVibeBlockDesignWizard
      || pendingVibeBlockDesignWizard.wizard_id !== answer.wizard_id
      || pendingVibeBlockDesignWizard.step !== answer.step
    ) return;

    await chatHandleUserResponse(
      chatActions,
      currentStep,
      '',
      answer.answer_text,
      [],
      answer,
      { clearUserInput: false, includeMentions: false }
    );
  };

  const handleSkipQuestion = () => {
    chatHandleSkipQuestion(chatActions);
  };

  const handleHelpClick = () => {
    chatHandleHelpClick(chatActions, currentStep);
  };

  const handleHelpSuggestionClick = (suggestion: string) => {
    chatHandleHelpSuggestionClick(chatActions, suggestion);
  };

  const handleProceed = () => {
    chatHandleProceed(chatActions);
  };

  const handleCopyMessage = useCallback(async (message: Message) => {
    if (!message || typeof message.content !== 'string') {
      return;
    }

    const plainText = message.type === 'user'
      ? message.content
      : message.content
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/?[^>]+(>|$)/g, '');

    const copied = await copyTextToClipboard(plainText);
    if (copied) {
      setCopiedMessageId(String(message.id));
    }
  }, []);

  const handleCopyMentionId = useCallback(async (value: string, copyKey: string) => {
    if (!value) {
      return;
    }

    const copied = await copyTextToClipboard(value);
    if (copied) {
      setCopiedMentionKey(copyKey);
    }
  }, []);

  const handleToggleReaction = useCallback(async (message: Message, action: MessageActionKey) => {
    if (!message) {
      return;
    }

    const messageId = typeof message.id === 'string' ? message.id : null;
    if (!messageId) {
      return;
    }

    const sessionId = selectedSession?.id;
    if (!sessionId) {
      return;
    }

    const token = authToken || localStorage.getItem('authToken');
    if (!token) {
      console.error('No auth token available for message reaction');
      return;
    }

    const currentLike = Boolean(message.isLiked);
    const currentDislike = Boolean(message.isDisliked);
    const shouldActivate = action === 'like' ? !currentLike : !currentDislike;
    const previousState = { isLiked: currentLike, isDisliked: currentDislike };

    updateActionLoading(messageId, action, true);

    setMessages((prev: Message[]) => prev.map((msg) => {
      if (msg.id !== message.id) {
        return msg;
      }

      if (action === 'like') {
        return {
          ...msg,
          isLiked: shouldActivate,
          isDisliked: shouldActivate ? false : msg.isDisliked
        };
      }

      return {
        ...msg,
        isDisliked: shouldActivate,
        isLiked: shouldActivate ? false : msg.isLiked
      };
    }));

    try {
      if (action === 'like') {
        await updateMessageLikeStatus(token, sessionId, messageId, shouldActivate);
        if (shouldActivate && previousState.isDisliked) {
          await updateMessageDislikeStatus(token, sessionId, messageId, false);
        }
      } else {
        await updateMessageDislikeStatus(token, sessionId, messageId, shouldActivate);
        if (shouldActivate && previousState.isLiked) {
          await updateMessageLikeStatus(token, sessionId, messageId, false);
        }
      }
    } catch (error) {
      console.error('Failed to update message reaction', error);
      setMessages((prev: Message[]) => prev.map((msg) => {
        if (msg.id !== message.id) {
          return msg;
        }

        return {
          ...msg,
          isLiked: previousState.isLiked,
          isDisliked: previousState.isDisliked
        };
      }));
    } finally {
      updateActionLoading(messageId, action, false);
    }
  }, [authToken, selectedSession?.id, updateActionLoading]);

  const handleDeleteMessage = useCallback(async (message: Message) => {
    if (!message) {
      return;
    }

    const messageId = typeof message.id === 'string' ? message.id : null;
    if (!messageId) {
      return;
    }

    const sessionId = selectedSession?.id;
    if (!sessionId) {
      return;
    }

    if (!window.confirm('Delete this message?')) {
      return;
    }

    const token = authToken || localStorage.getItem('authToken');
    if (!token) {
      console.error('No auth token available for message deletion');
      return;
    }

    updateActionLoading(messageId, 'delete', true);

    try {
      await deleteMessageById(token, sessionId, messageId);
      setMessages((prev: Message[]) => {
        const targetIndex = prev.findIndex((msg) => msg.id === message.id);
        if (targetIndex === -1) {
          return prev;
        }

        return prev.slice(0, targetIndex);
      });
    } catch (error) {
      console.error('Failed to delete message', error);
    } finally {
      updateActionLoading(messageId, 'delete', false);
    }
  }, [authToken, selectedSession?.id, updateActionLoading]);

  const applyUpdatedDecision = useCallback((decision: DecisionListItemType, messageDecision: MessageDecisionReferenceType) => {
    setDecisionDetailsById((prev) => ({
      ...prev,
      [decision.id]: decision
    }));

    setMessages((prevMessages) => {
      return prevMessages.map((message) => {
        if (!message.decisions || message.decisions.length === 0) {
          return message;
        }

        let hasChanges = false;
        const updatedDecisions = message.decisions.map((currentDecision) => {
          if (currentDecision.id !== messageDecision.id) {
            return currentDecision;
          }

          hasChanges = true;
          return {
            ...currentDecision,
            ...messageDecision
          };
        });

        if (!hasChanges) {
          return message;
        }

        return {
          ...message,
          decisions: updatedDecisions
        };
      });
    });
  }, []);

  const applyUpdatedMessageOfferPlanUpgrade = useCallback((messageId: string, offerPlanUpgrade: OfferPlanUpgradeType) => {
    setMessages((prevMessages) => {
      return prevMessages.map((message) => {
        if (String(message.id) !== messageId) {
          return message;
        }

        return {
          ...message,
          offer_plan_upgrade: offerPlanUpgrade
        };
      });
    });
  }, []);

  const applyPlanUpgradeResolvedForScope = useCallback((scope: PlanUpgradeResolutionScope) => {
    setDecisionDetailsById((prev) => {
      let hasChanges = false;
      const next = { ...prev };

      for (const [decisionId, decision] of Object.entries(prev)) {
        const upgradeRequired = getDecisionUpgradeRequired(decision);

        if (!isUpgradeRequiredInScope(upgradeRequired, scope)) {
          continue;
        }

        const updatedUpgradeRequired = {
          ...upgradeRequired,
          state: 'planUpgraded',
          ...(scope.stateUpdatedAt ? {
            stateUpdatedAt: scope.stateUpdatedAt,
            state_updated_at: scope.stateUpdatedAt
          } : {})
        } as UpgradeRequiredType;

        next[decisionId] = {
          ...decision,
          is_upgrade_required: false,
          upgradeRequired: updatedUpgradeRequired,
          ...(isRecordValue(decision.upgrade_required) ? { upgrade_required: updatedUpgradeRequired } : {})
        };
        hasChanges = true;
      }

      return hasChanges ? next : prev;
    });

    setMessages((prevMessages) => {
      return prevMessages.map((message) => {
        let hasChanges = false;
        let nextMessage = message;

        if (isOfferPlanUpgradeInScope(message.offer_plan_upgrade, scope)) {
          const currentOfferPlanUpgrade = message.offer_plan_upgrade;
          const nextStateUpdatedAt = scope.stateUpdatedAt ||
            currentOfferPlanUpgrade.state_updated_at ||
            currentOfferPlanUpgrade.stateUpdatedAt;

          if (currentOfferPlanUpgrade.state !== 'planUpgraded') {
            nextMessage = {
              ...nextMessage,
              offer_plan_upgrade: {
                ...currentOfferPlanUpgrade,
                state: 'planUpgraded',
                ...(nextStateUpdatedAt ? {
                  state_updated_at: nextStateUpdatedAt,
                  stateUpdatedAt: nextStateUpdatedAt
                } : {})
              }
            };
            hasChanges = true;
          }
        }

        if (message.decisions && message.decisions.length > 0) {
          let hasDecisionChanges = false;
          const nextDecisions = message.decisions.map((decisionReference) => {
            const decisionDetails = decisionDetailsById[decisionReference.id];
            const upgradeRequired = getDecisionUpgradeRequired(decisionDetails);

            if (!isUpgradeRequiredInScope(upgradeRequired, scope)) {
              return decisionReference;
            }

            if (decisionReference.is_upgrade_required === false) {
              return decisionReference;
            }

            hasDecisionChanges = true;
            return {
              ...decisionReference,
              is_upgrade_required: false
            };
          });

          if (hasDecisionChanges) {
            nextMessage = {
              ...nextMessage,
              decisions: nextDecisions
            };
            hasChanges = true;
          }
        }

        return hasChanges ? nextMessage : message;
      });
    });
  }, [decisionDetailsById]);

  const applyDecisionSnapshots = useCallback((decisions: DecisionListItemType[]) => {
    if (!Array.isArray(decisions) || decisions.length === 0) {
      return;
    }

    const messageDecisionsById = decisions.reduce<Record<string, MessageDecisionReferenceType>>((acc, decision) => {
      const upgradeRequired = getDecisionUpgradeRequired(decision);
      const isUpgradeRequiredReference = typeof decision.is_upgrade_required === 'boolean'
        ? decision.is_upgrade_required
        : false;

      acc[decision.id] = {
        id: decision.id,
        state: decision.state,
        is_upgrade_required: isUpgradeRequiredActive(upgradeRequired, isUpgradeRequiredReference),
        website_publish_state: decision.website_publish_state,
        ...(decision.annotations ? { annotations: decision.annotations } : {})
      };

      return acc;
    }, {});

    setDecisionDetailsById((prev) => {
      const next = { ...prev };
      for (const decision of decisions) {
        next[decision.id] = decision;
      }
      return next;
    });

    setMessages((prevMessages) => {
      return prevMessages.map((message) => {
        if (!message.decisions || message.decisions.length === 0) {
          return message;
        }

        let hasChanges = false;
        const updatedDecisions = message.decisions.map((currentDecision) => {
          const snapshot = messageDecisionsById[currentDecision.id];
          if (!snapshot) {
            return currentDecision;
          }

          const nextDecision = {
            ...currentDecision,
            ...snapshot
          };

          if (
            nextDecision.state !== currentDecision.state ||
            nextDecision.is_upgrade_required !== currentDecision.is_upgrade_required ||
            nextDecision.website_publish_state !== currentDecision.website_publish_state ||
            nextDecision.annotations?.hide_for_enduser !== currentDecision.annotations?.hide_for_enduser ||
            nextDecision.annotations?.auto_apply_decision !== currentDecision.annotations?.auto_apply_decision
          ) {
            hasChanges = true;
          }

          return nextDecision;
        });

        if (!hasChanges) {
          return message;
        }

        return {
          ...message,
          decisions: updatedDecisions
        };
      });
    });
  }, []);

  const refreshSessionDecisionsSnapshot = useCallback(async (token: string, sessionId: string) => {
    const loadedDecisions = await fetchDecisionsBySessionId(token, sessionId);
    applyDecisionSnapshots(loadedDecisions);
  }, [applyDecisionSnapshots]);

  const handleDecisionAccept = useCallback(async (decisionId: string) => {
    const token = authToken || localStorage.getItem('authToken');
    if (!token) {
      console.error('No auth token available for decision action');
      return;
    }

    const currentSessionId = selectedSession?.id;
    if (!currentSessionId) {
      console.error('No selected session available for decision action');
      return;
    }

    updateDecisionActionLoading(decisionId, 'accept', true);
    try {
      const response = await acceptDecisionById(token, currentSessionId, decisionId);
      applyUpdatedDecision(response.decision, response.messageDecision);
      try {
        await refreshSessionDecisionsSnapshot(token, currentSessionId);
      } catch (snapshotError) {
        console.error('Failed to refresh decision snapshot after accept', snapshotError);
      }
    } catch (error) {
      console.error('Failed to accept decision', error);
    } finally {
      updateDecisionActionLoading(decisionId, 'accept', false);
    }
  }, [authToken, applyUpdatedDecision, refreshSessionDecisionsSnapshot, selectedSession?.id, updateDecisionActionLoading]);

  const handleDecisionDecline = useCallback(async (decisionId: string) => {
    const token = authToken || localStorage.getItem('authToken');
    if (!token) {
      console.error('No auth token available for decision action');
      return;
    }

    const currentSessionId = selectedSession?.id;
    if (!currentSessionId) {
      console.error('No selected session available for decision action');
      return;
    }

    updateDecisionActionLoading(decisionId, 'decline', true);
    try {
      const response = await declineDecisionById(token, currentSessionId, decisionId);
      applyUpdatedDecision(response.decision, response.messageDecision);
      try {
        await refreshSessionDecisionsSnapshot(token, currentSessionId);
      } catch (snapshotError) {
        console.error('Failed to refresh decision snapshot after decline', snapshotError);
      }
    } catch (error) {
      console.error('Failed to decline decision', error);
    } finally {
      updateDecisionActionLoading(decisionId, 'decline', false);
    }
  }, [authToken, applyUpdatedDecision, refreshSessionDecisionsSnapshot, selectedSession?.id, updateDecisionActionLoading]);

  const handleDecisionPublish = useCallback(async (
    decisionId: string,
    currentWebsitePublishState: DecisionWebsitePublishStateType
  ) => {
    if (
      currentWebsitePublishState === 'not_applicable' ||
      currentWebsitePublishState === 'published'
    ) {
      return;
    }

    const token = authToken || localStorage.getItem('authToken');
    if (!token) {
      console.error('No auth token available for decision publish action');
      return;
    }

    const currentSessionId = selectedSession?.id;
    if (!currentSessionId) {
      console.error('No selected session available for decision publish action');
      return;
    }

    const setWebsitePublishState = async (state: DecisionWebsitePublishStateType) => {
      const response = await setDecisionWebsitePublishStateById(
        token,
        currentSessionId,
        decisionId,
        state
      );
      applyUpdatedDecision(response.decision, response.messageDecision);
    };

    updateDecisionActionLoading(decisionId, 'publish', true);
    try {
      if (currentWebsitePublishState === 'publishing') {
        await setWebsitePublishState('published');
        return;
      }

      await setWebsitePublishState('publishing');
    } catch (error) {
      console.error('Failed to update decision website publish state', error);
      return;
    } finally {
      updateDecisionActionLoading(decisionId, 'publish', false);
    }

    await wait(2000);

    updateDecisionActionLoading(decisionId, 'publish', true);
    try {
      await setWebsitePublishState('published');
    } catch (error) {
      console.error('Failed to mark decision website publish state as published', error);
    } finally {
      updateDecisionActionLoading(decisionId, 'publish', false);
    }
  }, [authToken, applyUpdatedDecision, selectedSession?.id, updateDecisionActionLoading]);

  const handleCloseDecisionUserInputModal = useCallback(() => {
    setDecisionUserInputModal(null);
  }, []);

  const handleOpenUpgradeRequiredModal = useCallback(async (decisionId: string) => {
    const token = authToken || localStorage.getItem('authToken');
    if (!token) {
      console.error('No auth token available for upgradeRequired action');
      return;
    }

    const currentSessionId = selectedSession?.id;
    if (!currentSessionId) {
      console.error('No selected session available for upgradeRequired action');
      return;
    }

    setUpgradeRequiredOpenLoadingByDecisionId((prev) => ({
      ...prev,
      [decisionId]: true
    }));

    try {
      const response = await setDecisionUpgradeRequiredStateById(
        token,
        currentSessionId,
        decisionId,
        'upgradePlanPopupInProgress'
      );
      applyUpdatedDecision(response.decision, response.messageDecision);
      setUpgradeRequiredModal({
        decisionId,
        isSubmitting: false,
        activeAction: null,
        error: null
      });
    } catch (error) {
      console.error('Failed to update decision upgradeRequired state', error);
    } finally {
      setUpgradeRequiredOpenLoadingByDecisionId((prev) => ({
        ...prev,
        [decisionId]: false
      }));
    }
  }, [authToken, applyUpdatedDecision, selectedSession?.id]);

  const handleUpgradeRequiredModalAction = useCallback(async (action: 'cancel' | 'confirm') => {
    if (!upgradeRequiredModal) {
      return;
    }

    const token = authToken || localStorage.getItem('authToken');
    if (!token) {
      setUpgradeRequiredModal((prev) => prev
        ? { ...prev, error: 'No auth token available for upgradeRequired action.' }
        : prev);
      return;
    }

    const currentSessionId = selectedSession?.id;
    if (!currentSessionId) {
      setUpgradeRequiredModal((prev) => prev
        ? { ...prev, error: 'No selected session available for upgradeRequired action.' }
        : prev);
      return;
    }

    const decisionId = upgradeRequiredModal.decisionId;
    const targetState = action === 'cancel'
      ? 'upgradePlanPopupNotShown'
      : 'planUpgraded';

    setUpgradeRequiredModal((prev) => prev
      ? { ...prev, isSubmitting: true, activeAction: action, error: null }
      : prev);

    try {
      const response = await setDecisionUpgradeRequiredStateById(
        token,
        currentSessionId,
        decisionId,
        targetState
      );
      applyUpdatedDecision(response.decision, response.messageDecision);
      if (targetState === 'planUpgraded') {
        const scope = buildPlanUpgradeResolutionScopeFromDecision(response.decision);
        if (scope) {
          applyPlanUpgradeResolvedForScope(scope);
        }
        try {
          await refreshSessionDecisionsSnapshot(token, currentSessionId);
        } catch (snapshotError) {
          console.error('Failed to refresh decision snapshot after upgradeRequired resolution', snapshotError);
        }
      }
      setUpgradeRequiredModal(null);
    } catch (error) {
      console.error('Failed to update decision upgradeRequired state', error);
      setUpgradeRequiredModal((prev) => prev
        ? {
          ...prev,
          isSubmitting: false,
          activeAction: null,
          error: getModalErrorMessage(error, 'Failed to update upgrade state. Please try again.')
        }
        : prev);
    }
  }, [
    authToken,
    applyPlanUpgradeResolvedForScope,
    applyUpdatedDecision,
    refreshSessionDecisionsSnapshot,
    selectedSession?.id,
    upgradeRequiredModal
  ]);

  const handleOpenOfferPlanUpgradeModal = useCallback(async (messageId: string) => {
    const token = authToken || localStorage.getItem('authToken');
    if (!token) {
      console.error('No auth token available for offer_plan_upgrade action');
      return;
    }

    const currentSessionId = selectedSession?.id;
    if (!currentSessionId) {
      console.error('No selected session available for offer_plan_upgrade action');
      return;
    }

    setOfferPlanUpgradeOpenLoadingByMessageId((prev) => ({
      ...prev,
      [messageId]: true
    }));

    try {
      const response = await setMessageOfferPlanUpgradeStateById(
        token,
        currentSessionId,
        messageId,
        'upgradePlanPopupInProgress'
      );
      applyUpdatedMessageOfferPlanUpgrade(messageId, response.messageOfferPlanUpgrade);
      setOfferPlanUpgradeModal({
        messageId,
        isSubmitting: false,
        activeAction: null,
        error: null
      });
    } catch (error) {
      console.error('Failed to update message offer_plan_upgrade state', error);
    } finally {
      setOfferPlanUpgradeOpenLoadingByMessageId((prev) => ({
        ...prev,
        [messageId]: false
      }));
    }
  }, [authToken, applyUpdatedMessageOfferPlanUpgrade, selectedSession?.id]);

  const handleOfferPlanUpgradeModalAction = useCallback(async (action: 'cancel' | 'confirm') => {
    if (!offerPlanUpgradeModal) {
      return;
    }

    const token = authToken || localStorage.getItem('authToken');
    if (!token) {
      setOfferPlanUpgradeModal((prev) => prev
        ? { ...prev, error: 'No auth token available for offer_plan_upgrade action.' }
        : prev);
      return;
    }

    const currentSessionId = selectedSession?.id;
    if (!currentSessionId) {
      setOfferPlanUpgradeModal((prev) => prev
        ? { ...prev, error: 'No selected session available for offer_plan_upgrade action.' }
        : prev);
      return;
    }

    const messageId = offerPlanUpgradeModal.messageId;
    const targetState = action === 'cancel'
      ? 'upgradePlanPopupNotShown'
      : 'planUpgraded';

    setOfferPlanUpgradeModal((prev) => prev
      ? { ...prev, isSubmitting: true, activeAction: action, error: null }
      : prev);

    try {
      const response = await setMessageOfferPlanUpgradeStateById(
        token,
        currentSessionId,
        messageId,
        targetState
      );
      applyUpdatedMessageOfferPlanUpgrade(messageId, response.messageOfferPlanUpgrade);
      if (targetState === 'planUpgraded') {
        const scope = buildPlanUpgradeResolutionScopeFromOffer(response.messageOfferPlanUpgrade);
        if (scope) {
          applyPlanUpgradeResolvedForScope(scope);
        }
        try {
          await refreshSessionDecisionsSnapshot(token, currentSessionId);
        } catch (snapshotError) {
          console.error('Failed to refresh decision snapshot after offer_plan_upgrade resolution', snapshotError);
        }
      }
      setOfferPlanUpgradeModal(null);
    } catch (error) {
      console.error('Failed to update message offer_plan_upgrade state', error);
      setOfferPlanUpgradeModal((prev) => prev
        ? {
          ...prev,
          isSubmitting: false,
          activeAction: null,
          error: getModalErrorMessage(error, 'Failed to update upgrade state. Please try again.')
        }
        : prev);
    }
  }, [
    authToken,
    applyPlanUpgradeResolvedForScope,
    applyUpdatedMessageOfferPlanUpgrade,
    offerPlanUpgradeModal,
    refreshSessionDecisionsSnapshot,
    selectedSession?.id
  ]);

  const handleDecisionUserInput = useCallback((decision: MessageDecisionReferenceType, decisionDetails?: DecisionListItemType) => {
    if (!decisionDetails) {
      console.error('Decision details are not loaded yet. Please try again in a moment.');
      return;
    }

    setDecisionUserInputModal(buildDecisionUserInputModalState(decision, decisionDetails));
  }, []);

  const handleDecisionUserInputSubmit = useCallback(async () => {
    if (!decisionUserInputModal) {
      return;
    }

    const decisionId = decisionUserInputModal.decisionId;
    const token = authToken || localStorage.getItem('authToken');
    if (!token) {
      setDecisionUserInputModal((prev) => prev
        ? { ...prev, error: 'No auth token available for decision action.' }
        : prev);
      return;
    }

    const currentSessionId = selectedSession?.id;
    if (!currentSessionId) {
      setDecisionUserInputModal((prev) => prev
        ? { ...prev, error: 'No selected session available for decision action.' }
        : prev);
      return;
    }

    let payload: {
      valueType: DecisionValueType;
      value_boolean?: boolean;
      value_number?: number;
      value_short_text?: string;
      value_long_text?: string;
    };

    if (decisionUserInputModal.valueType === 'boolean') {
      payload = {
        valueType: 'boolean',
        value_boolean: decisionUserInputModal.booleanValue
      };
    } else if (decisionUserInputModal.valueType === 'number') {
      const normalizedNumber = decisionUserInputModal.numberValue.trim();
      const parsedNumber = Number(normalizedNumber);

      if (!normalizedNumber || !Number.isFinite(parsedNumber)) {
        setDecisionUserInputModal((prev) => prev
          ? { ...prev, error: 'Please enter a valid number.' }
          : prev);
        return;
      }

      payload = {
        valueType: 'number',
        value_number: parsedNumber
      };
    } else if (decisionUserInputModal.valueType === 'shortText') {
      const normalizedText = decisionUserInputModal.shortTextValue.trim();

      if (normalizedText.length === 0) {
        setDecisionUserInputModal((prev) => prev
          ? { ...prev, error: 'Value cannot be empty.' }
          : prev);
        return;
      }

      if (normalizedText.length > MAX_DECISION_SHORT_TEXT_LENGTH) {
        setDecisionUserInputModal((prev) => prev
          ? { ...prev, error: `Short text value must be at most ${MAX_DECISION_SHORT_TEXT_LENGTH} characters.` }
          : prev);
        return;
      }

      payload = {
        valueType: 'shortText',
        value_short_text: normalizedText
      };
    } else {
      const normalizedText = decisionUserInputModal.longTextValue.trim();

      if (normalizedText.length === 0) {
        setDecisionUserInputModal((prev) => prev
          ? { ...prev, error: 'Value cannot be empty.' }
          : prev);
        return;
      }

      if (normalizedText.length > MAX_DECISION_LONG_TEXT_LENGTH) {
        setDecisionUserInputModal((prev) => prev
          ? { ...prev, error: `Long text value must be at most ${MAX_DECISION_LONG_TEXT_LENGTH} characters.` }
          : prev);
        return;
      }

      payload = {
        valueType: 'longText',
        value_long_text: normalizedText
      };
    }

    updateDecisionActionLoading(decisionId, 'userInput', true);

    try {
      const response = await setDecisionUserInputById(token, currentSessionId, decisionId, payload);
      applyUpdatedDecision(response.decision, response.messageDecision);
      setDecisionUserInputModal(null);
    } catch (error) {
      console.error('Failed to set user value for decision', error);
      setDecisionUserInputModal((prev) => prev
        ? { ...prev, error: 'Failed to save user input. Please try again.' }
        : prev);
    } finally {
      updateDecisionActionLoading(decisionId, 'userInput', false);
    }
  }, [authToken, applyUpdatedDecision, decisionUserInputModal, selectedSession?.id, updateDecisionActionLoading]);

  useEffect(() => {
    if (!decisionUserInputModal) {
      return;
    }

    const handleEscapeKey = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') {
        return;
      }

      event.preventDefault();
      setDecisionUserInputModal(null);
    };

    window.addEventListener('keydown', handleEscapeKey);

    return () => {
      window.removeEventListener('keydown', handleEscapeKey);
    };
  }, [decisionUserInputModal]);

  const handleImageClick = () => {
    if (imageInputRef.current) {
      imageInputRef.current.click();
    }
  };

  const extractImagesFromClipboard = useCallback((clipboardData: DataTransfer | null): File[] => {
    if (!clipboardData) return [];

    const imageFiles: File[] = [];

    if (clipboardData.items && clipboardData.items.length > 0) {
      for (let i = 0; i < clipboardData.items.length; i++) {
        const item = clipboardData.items[i];
        if (item.kind === 'file' && item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file && isImageFile(file)) {
            imageFiles.push(file);
          }
        }
      }
    }

    if (imageFiles.length === 0 && clipboardData.files && clipboardData.files.length > 0) {
      for (let i = 0; i < clipboardData.files.length; i++) {
        const file = clipboardData.files[i];
        if (isImageFile(file)) {
          imageFiles.push(file);
        }
      }
    }

    return imageFiles;
  }, []);

  const processImageFiles = useCallback(async (files: FileList | File[]) => {
    if (isSiteBrief) return;
    if (!files || files.length === 0) return;

    const newImages: ImageData[] = [];
    const oversizedFileNames: string[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (isImageFile(file)) {
        if (!isImageSizeValid(file)) {
          oversizedFileNames.push(file.name || 'Pasted image');
          continue;
        }

        try {
          let fileToProcess = file;
          // Clipboard screenshots often lack a unique filename.
          if (!file.name || file.name === 'blob' || file.name === 'image.png') {
            const extension = file.type.split('/')[1] || 'png';
            fileToProcess = new File(
              [file],
              `screenshot-${Date.now()}-${i + 1}.${extension}`,
              { type: file.type }
            );
          }
          const imageData = await fileToImageData(fileToProcess);
          newImages.push(imageData);
        } catch (error) {
          console.error('Error processing image:', error);
        }
      }
    }

    if (newImages.length > 0) {
      setSelectedImages(prev => [...prev, ...newImages]);
    }

    if (oversizedFileNames.length > 0) {
      const fileNamesText = oversizedFileNames.join(', ');
      const sizeErrorMessage: Message = {
        id: `error-image-size-${Date.now()}`,
        type: 'error',
        content: `Image upload failed. Files exceed ${MAX_IMAGE_UPLOAD_SIZE_LABEL}: ${fileNamesText}`
      };
      setMessages((prev: Message[]) => [...prev, sizeErrorMessage]);
    }
  }, [isSiteBrief]);

  const handleImageSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      await processImageFiles(files);
    }

    // Reset the input so the same file can be selected again
    event.target.value = '';
  };

  useEffect(() => {
    const handleGlobalPaste = async (event: ClipboardEvent) => {
      if (isChatFinished) return;

      // The textarea's onPaste handler handles this case.
      if (document.activeElement === inputRef.current) return;

      // Do not intercept paste while the user is editing another field.
      const activeElement = document.activeElement;
      if (
        activeElement &&
        (activeElement.tagName === 'INPUT' ||
          activeElement.tagName === 'TEXTAREA' ||
          (activeElement as HTMLElement).isContentEditable)
      ) {
        return;
      }

      const imageFiles = extractImagesFromClipboard(event.clipboardData);
      if (imageFiles.length > 0) {
        event.preventDefault();
        await processImageFiles(imageFiles);
        inputRef.current?.focus();
      }
    };

    window.addEventListener('paste', handleGlobalPaste);
    return () => {
      window.removeEventListener('paste', handleGlobalPaste);
    };
  }, [isChatFinished, extractImagesFromClipboard, processImageFiles]);

  const handleRemoveImage = (index: number) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleOpenImagePreview = useCallback((image: ImageData) => {
    setPreviewImage(image);
  }, []);

  const handleOpenDecisionImagePreview = useCallback((imageUrl: string, imageName: string) => {
    setPreviewImage({
      url: imageUrl,
      name: imageName,
      size: ''
    });
  }, []);

  const handleCloseImagePreview = useCallback(() => {
    setPreviewImage(null);
  }, []);

  const isDecisionUserInputSubmitting = decisionUserInputModal
    ? Boolean(decisionActionLoading[decisionUserInputModal.decisionId]?.userInput)
    : false;

  // Calculate bottom spacing based on whether suggestions and pending image previews are shown
  const baseBottomSpacing = showSuggestions
    ? (isMobile ? 260 : 280)
    : (isMobile ? 160 : 180);
  const pendingImageRows = selectedImages.length > 0
    ? Math.ceil(selectedImages.length / (isMobile ? 3 : 5))
    : 0;
  const pendingImagesSpacing = pendingImageRows > 0
    ? (isMobile ? 54 : 46) + (pendingImageRows * (isMobile ? 112 : 104))
    : 0;
  const inputHeightSpacing = chatInputHeight - CHAT_INPUT_MIN_HEIGHT;
  const bottomSpacing = `${baseBottomSpacing + pendingImagesSpacing + inputHeightSpacing}px`;

  // Determine if a message is the current step's question message
  const isCurrentQuestion = (message: Message) => {
    return (
      currentStep > 0 &&
      currentStep < chatFlow.length - 1 &&
      message.id === chatFlow[currentStep].id &&
      message.type === 'question'
    );
  };

  return (
    <motion.div
      className="agent-chat flex flex-col h-full relative w-full max-w-[1078px] mx-auto"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Messages Container with Proper Padding */}
      <div
        className={`agent-messages flex-1 overflow-y-auto overflow-x-hidden px-12 py-8 chat-container-appear custom-scrollbar ${isMobile ? 'mobile-chat-window' : ''} w-full`}
        ref={chatContainerRef}
        style={{ paddingBottom: bottomSpacing, '--agent-message-clearance': bottomSpacing } as CSSProperties}
      >
        <div className={`${isMobile ? 'w-full' : 'max-w-3xl'} mx-auto`}>
          <AnimatePresence mode="popLayout">
            {messages.map((message, index) => {
              if (message.type === 'user' && message.vibe_block_design_wizard_answer) {
                return null;
              }

              if (message.vibe_block_design_wizard_question) {
                const wizardProgress = vibeBlockDesignWizardProgress.find(
                  (progress) => progress.wizardId === message.vibe_block_design_wizard_question?.wizard_id
                );
                if (wizardProgress && wizardProgress.latestMessageId !== message.id) {
                  return null;
                }
              }

              const isWizardMessage = Boolean(message.vibe_block_design_wizard_question);
              const messageKey = isWizardMessage
                ? `vibe-block-design-wizard-${message.vibe_block_design_wizard_question?.wizard_id}`
                : String(message.id);
              const backendMessageId = typeof message.id === 'string' ? message.id : undefined;
              const isHighlightedMessage = backendMessageId === highlightedMessageId;
              const actionState = backendMessageId ? messageActionLoading[backendMessageId] : undefined;
              const isCopied = copiedMessageId === String(message.id);
              const isErrorMessage = message.type === 'error';
              const actionsDisabled = !selectedSession?.id || typeof message.id !== 'string' || String(message.id).startsWith('error-') || isErrorMessage || (typeof message.id === 'string' && message.id.startsWith('user-'));
              const typedContent = typingState?.id === String(message.id) ? typingState.content : undefined;
              const htmlContent = formatMessageHtml(message, typedContent);
              const websiteMentionId = getWebsiteMentionId(message.mentions);
              const websiteMentionLabel = getWebsiteMentionLabel(message.mentions, message.mentionWebsiteName) || 'Website';
              const pageMentionItems = message.mentions?.pages && message.mentions.pages.length > 0
                ? message.mentions.pages
                : [];
              const blockMentionItems = message.mentions?.blocks && message.mentions.blocks.length > 0
                ? message.mentions.blocks
                : [];
              const elementMentionItems = message.mentions?.elements && message.mentions.elements.length > 0
                ? message.mentions.elements
                : [];
              const messageDecisionItems = message.decisions && message.decisions.length > 0
                ? message.decisions
                : [];
              const messageOfferPlanUpgrade = message.offer_plan_upgrade;
              const hasOfferPlanUpgrade = isOfferPlanUpgradeActive(messageOfferPlanUpgrade);
              const showOfferPlanUpgradeButton = shouldShowOfferPlanUpgradeButton(messageOfferPlanUpgrade);
              const isOfferPlanUpgradeButtonLoading = backendMessageId
                ? Boolean(offerPlanUpgradeOpenLoadingByMessageId[backendMessageId])
                : false;

              return (
                <motion.div
                  key={messageKey}
                  data-message-id={backendMessageId}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  className={`agent-message mb-8 max-w-full scroll-mt-8 rounded-2xl transition-shadow duration-700 ${message.type === 'user' ? 'flex justify-end' : 'flex justify-start'} ${isHighlightedMessage ? 'ring-2 ring-[#165BFB]/40 ring-offset-4' : ''}`}
                >
                  {message.type === 'user' ? (
                    <div className="agent-message-row flex items-end gap-3">
                      <motion.div
                        className="bg-[#eaf0ff] p-4 rounded-tl-lg rounded-tr-lg rounded-bl-lg message-user-bubble break-words max-w-full"
                        initial={{ x: 20, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ type: "spring", stiffness: 500, damping: 30 }}
                      >
                        {(websiteMentionId || pageMentionItems.length > 0 || blockMentionItems.length > 0 || elementMentionItems.length > 0) && (
                          <div className="agent-context-card mb-3 rounded-2xl border border-[#ccdcff] bg-[#f7faff] p-3">
                            <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-[#4f6ea8]">
                              Mentioned context
                            </div>
                            <div className="space-y-2">
                              {websiteMentionId && (
                                <MentionEntityRow
                                  entityType="Website"
                                  name={websiteMentionLabel}
                                  id={websiteMentionId}
                                  copyKey={`${messageKey}-website`}
                                  copiedMentionKey={copiedMentionKey}
                                  onCopy={handleCopyMentionId}
                                />
                              )}
                              {pageMentionItems.map((pageMention, pageIndex) => (
                                <MentionEntityRow
                                  key={`${pageMention.id}-${pageIndex}`}
                                  entityType="Page"
                                  name={getPageMentionLabel(pageMention)}
                                  id={pageMention.id}
                                  isHomePage={pageMention.is_home_page}
                                  copyKey={`${messageKey}-page-${pageMention.id}-${pageIndex}`}
                                  copiedMentionKey={copiedMentionKey}
                                  onCopy={handleCopyMentionId}
                                />
                              ))}
                              {blockMentionItems.map((blockMention, blockIndex) => (
                                <MentionEntityRow
                                  key={`${blockMention.id}-${blockIndex}`}
                                  entityType="Block"
                                  name={getBlockMentionLabel(blockMention)}
                                  id={blockMention.id}
                                  copyKey={`${messageKey}-block-${blockMention.id}-${blockIndex}`}
                                  copiedMentionKey={copiedMentionKey}
                                  onCopy={handleCopyMentionId}
                                />
                              ))}
                              {elementMentionItems.map((elementMention, elementIndex) => (
                                <MentionEntityRow
                                  key={`${elementMention.id}-${elementIndex}`}
                                  entityType="Element"
                                  name={getElementMentionLabel(elementMention)}
                                  id={elementMention.id}
                                  copyKey={`${messageKey}-element-${elementMention.id}-${elementIndex}`}
                                  copiedMentionKey={copiedMentionKey}
                                  onCopy={handleCopyMentionId}
                                />
                              ))}
                            </div>
                          </div>
                        )}
                        {hasOfferPlanUpgrade && backendMessageId && (
                          <UpgradeRequiredNotice
                            showUpgradeButton={showOfferPlanUpgradeButton}
                            isUpgradeButtonLoading={isOfferPlanUpgradeButtonLoading}
                            onUpgradeClick={() => {
                              handleOpenOfferPlanUpgradeModal(backendMessageId).catch((error) => {
                                console.error('Failed to open offer_plan_upgrade modal', error);
                              });
                            }}
                          />
                        )}
                        {messageDecisionItems.length > 0 && (
                          <div className="agent-context-card mb-3 rounded-2xl border border-[#ccdcff] bg-[#f7faff] p-3">
                            <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-[#4f6ea8]">
                              Decisions
                            </div>
                            <div className="space-y-2.5">
                              {messageDecisionItems.map((decision) => {
                                const decisionDetails = decisionDetailsById[decision.id];
                                const decisionMetadataItems = getDecisionMetadataItems(decisionDetails);
                                const decisionState = decisionDetails?.state ?? decision.state;
                                const upgradeRequired = getDecisionUpgradeRequired(decisionDetails);
                                const hasUpgradeRequired = isUpgradeRequiredActive(upgradeRequired, decision.is_upgrade_required === true);
                                const showUpgradeButton = shouldShowUpgradeButton(upgradeRequired);
                                const isUpgradeButtonLoading = Boolean(upgradeRequiredOpenLoadingByDecisionId[decision.id]);
                                const isAcceptLoading = Boolean(decisionActionLoading[decision.id]?.accept);
                                const isDeclineLoading = Boolean(decisionActionLoading[decision.id]?.decline);
                                const isUserInputLoading = Boolean(decisionActionLoading[decision.id]?.userInput);
                                const isPublishLoading = Boolean(decisionActionLoading[decision.id]?.publish);
                                const showHiddenForEndUserBanner = shouldShowHiddenForEndUserBanner(decision, decisionDetails);
                                const autoApplyDecision = isAutoApplyDecision(decision, decisionDetails);
                                const canAcceptOrDecline = !autoApplyDecision && (decisionState === 'created' || decisionState === 'userOwnValueSet') && !hasUpgradeRequired;
                                const canSetUserInput = !autoApplyDecision && !isDecisionFinalState(decisionState) && Boolean(decisionDetails) && !hasUpgradeRequired;
                                const websitePublishState = getDecisionWebsitePublishState(decisionDetails, decision);
                                const showPublishButton = websitePublishState !== 'not_applicable';
                                const isPublishDisabled = websitePublishState === 'published' || isPublishLoading;

                                return (
                                  <div
                                    key={decision.id}
                                    className="rounded-xl border border-[#d9e6ff] bg-white px-3 py-2.5"
                                  >
                                    {showHiddenForEndUserBanner && (
                                      <div className="mb-2 rounded-lg border border-[#f5d78e] bg-[#fff8e6] px-3 py-2 text-[11px] font-semibold text-[#8a5a00]">
                                        Decision will be hidden from enduser
                                      </div>
                                    )}
                                    {hasUpgradeRequired && (
                                      <UpgradeRequiredNotice
                                        showUpgradeButton={showUpgradeButton}
                                        isUpgradeButtonLoading={isUpgradeButtonLoading}
                                        onUpgradeClick={() => {
                                          handleOpenUpgradeRequiredModal(decision.id).catch((error) => {
                                            console.error('Failed to open upgradeRequired modal', error);
                                          });
                                        }}
                                      />
                                    )}
                                    <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2">
                                      <span className="text-[13px] font-semibold text-[#27344a]">
                                        {formatDecisionLabel(decisionDetails)}
                                      </span>
                                      <span className="rounded-full bg-[#eef4ff] px-2 py-0.5 text-[11px] font-semibold text-[#2f5ca8]">
                                        {formatDecisionStateLabel(decisionState)}
                                      </span>
                                    </div>
                                    {decisionMetadataItems.length > 0 && (
                                      <div className="mb-2 space-y-1.5">
                                        {decisionMetadataItems.map((scopeItem) => (
                                          <DecisionScopeRow
                                            key={`${decision.id}-${scopeItem.key}`}
                                            label={scopeItem.label}
                                            value={scopeItem.value}
                                            copyKey={`${messageKey}-decision-${decision.id}-${scopeItem.key}`}
                                            copiedMentionKey={copiedMentionKey}
                                            onCopy={handleCopyMentionId}
                                          />
                                        ))}
                                      </div>
                                    )}
                                    <DecisionValueRow
                                      decisionId={decision.id}
                                      details={decisionDetails}
                                      decisionImagePreview={decisionImagePreviewById[decision.id]}
                                      onOpenDecisionImagePreview={handleOpenDecisionImagePreview}
                                    />
                                    {hasUpgradeRequired && (
                                      <div className="mb-2 text-[11px] font-medium text-[#b45309]">
                                        Upgrade is required before decision actions are available.
                                      </div>
                                    )}
                                    <div className="flex flex-wrap items-center gap-2">
                                      <button
                                        type="button"
                                        className={`rounded-full border px-3 py-1 text-[12px] font-semibold transition-colors ${
                                          canAcceptOrDecline
                                            ? 'border-[#b8d1ff] bg-[#eef4ff] text-[#2457cc] hover:border-[#8bb1ff]'
                                            : 'border-[#e5e7eb] bg-[#f9fafb] text-[#9ca3af] cursor-not-allowed'
                                        }`}
                                        disabled={!canAcceptOrDecline || isAcceptLoading}
                                        onClick={() => {
                                          handleDecisionAccept(decision.id).catch((error) => console.error('Failed to accept decision', error));
                                        }}
                                      >
                                        {getDecisionAcceptButtonLabel(decisionDetails, isAcceptLoading)}
                                      </button>
                                      <button
                                        type="button"
                                        className={`rounded-full border px-3 py-1 text-[12px] font-semibold transition-colors ${
                                          canAcceptOrDecline
                                            ? 'border-[#f5c2c7] bg-[#fff5f6] text-[#b4232f] hover:border-[#ee9aa3]'
                                            : 'border-[#e5e7eb] bg-[#f9fafb] text-[#9ca3af] cursor-not-allowed'
                                        }`}
                                        disabled={!canAcceptOrDecline || isDeclineLoading}
                                        onClick={() => {
                                          handleDecisionDecline(decision.id).catch((error) => console.error('Failed to decline decision', error));
                                        }}
                                      >
                                        {getDecisionDeclineButtonLabel(decisionDetails, isDeclineLoading)}
                                      </button>
                                      <button
                                        type="button"
                                        className={`rounded-full border px-3 py-1 text-[12px] font-semibold transition-colors ${
                                          canSetUserInput
                                            ? 'border-[#d7e3ff] bg-[#f8fbff] text-[#4f6ea8] hover:border-[#97b9ff]'
                                            : 'border-[#e5e7eb] bg-[#f9fafb] text-[#9ca3af] cursor-not-allowed'
                                        }`}
                                        disabled={!canSetUserInput || isUserInputLoading}
                                        onClick={() => {
                                          handleDecisionUserInput(decision, decisionDetails);
                                        }}
                                      >
                                        {isUserInputLoading ? 'Saving...' : 'User input'}
                                      </button>
                                      {showPublishButton && (
                                        <button
                                          type="button"
                                          className={`rounded-full border px-3 py-1 text-[12px] font-semibold transition-colors ${
                                            isPublishDisabled
                                              ? 'border-[#e5e7eb] bg-[#f9fafb] text-[#9ca3af] cursor-not-allowed'
                                              : 'border-[#bbf7d0] bg-[#f0fdf4] text-[#15803d] hover:border-[#86efac]'
                                          }`}
                                          disabled={isPublishDisabled}
                                          onClick={() => {
                                            handleDecisionPublish(decision.id, websitePublishState).catch((error) => {
                                              console.error('Failed to publish decision website state', error);
                                            });
                                          }}
                                        >
                                          {getDecisionPublishButtonLabel(websitePublishState, isPublishLoading)}
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        {/* Display images if present */}
                        {(message as UserMessage).images && (message as UserMessage).images!.length > 0 && (
                          <div className="agent-context-card mb-3 rounded-2xl border border-[#ccdcff] bg-[#f7faff] p-3">
                            <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-[#4f6ea8]">
                              Uploaded images
                            </div>
                            <div className="flex flex-wrap gap-2.5">
                            {(message as UserMessage).images!.map((img, imgIndex) => (
                              <button
                                key={`${img.url}-${imgIndex}`}
                                type="button"
                                className="group relative rounded-xl border border-[#d9e6ff] bg-white p-1.5 text-left transition-colors hover:border-[#97b9ff]"
                                onClick={() => handleOpenImagePreview(img)}
                                aria-label={`Open preview for ${img.name || `image ${imgIndex + 1}`}`}
                              >
                                <img
                                  src={img.url}
                                  alt={img.name || `Image ${imgIndex + 1}`}
                                  className="h-28 w-28 rounded-lg object-cover transition-opacity group-hover:opacity-95 sm:h-32 sm:w-32"
                                />
                                <div className="mt-1 max-w-[128px] truncate text-[10px] font-medium text-[#5f6f89]">
                                  {img.name || `Image ${imgIndex + 1}`}
                                </div>
                              </button>
                            ))}
                            </div>
                          </div>
                        )}
                        {/* Display text content */}
                        {message.content && (
                          <div className="agent-user-text text-[#333333] text-[16px] text-center">
                            <p>{message.content}</p>
                          </div>
                        )}
                        <MessageRouterPanel router={message.router} isLoading={Boolean(message.isRouterLoading)} />
                        <MessageActionBar
                          message={message}
                          onCopy={handleCopyMessage}
                          onToggleReaction={handleToggleReaction}
                          onDelete={handleDeleteMessage}
                          loading={actionState}
                          isCopied={isCopied}
                          actionsDisabled={actionsDisabled}
                        />
                      </motion.div>
                      <motion.div
                        className="agent-user-avatar bg-[#464c5c] rounded-full size-10 flex items-center justify-center"
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ delay: 0.1 }}
                      >
                        <p className="font-bold text-[#f7f8fa] text-[16px]">DL</p>
                      </motion.div>
                    </div>
                  ) : message.type === 'error' ? (
                    <div className="flex flex-col">
                      <div className="flex items-start gap-3">
                        {/* Check if this is a limit exceeded error - display with warning style */}
                        {message.content.includes('Limit Exceeded') ? (
                          <>
                            <motion.div
                              className="rounded-full size-10 flex items-center justify-center relative overflow-hidden mt-1 bg-[#fef3c7]"
                              initial={{ scale: 0.8, opacity: 0 }}
                              animate={{ scale: 1, opacity: 1 }}
                              transition={{ delay: 0.1 }}
                            >
                              <div className="size-6">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" fill="#f59e0b"/>
                                </svg>
                              </div>
                            </motion.div>
                            <div className="agent-message-body flex flex-col max-w-full sm:max-w-[598px]">
                              <motion.div
                                className="bg-[#fffbeb] p-4 rounded-tr-lg rounded-br-lg rounded-bl-lg shadow-[0px_1px_6px_0px_rgba(245,158,11,0.3)] message-warning-bubble break-words max-w-full border border-[#fde68a]"
                                initial={{ x: -20, opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                transition={{ type: "spring", stiffness: 500, damping: 30 }}
                              >
                                <div className="text-[#92400e] text-[16px] whitespace-pre-line">
                                  <p>{message.content}</p>
                                </div>
                                <MessageActionBar
                                  message={message}
                                  onCopy={handleCopyMessage}
                                  onToggleReaction={handleToggleReaction}
                                  onDelete={handleDeleteMessage}
                                  loading={actionState}
                                  isCopied={isCopied}
                                  actionsDisabled={actionsDisabled}
                                />
                              </motion.div>
                            </div>
                          </>
                        ) : (
                          <>
                            <motion.div
                              className="rounded-full size-10 flex items-center justify-center relative overflow-hidden mt-1 bg-[#fff0f0]"
                              initial={{ scale: 0.8, opacity: 0 }}
                              animate={{ scale: 1, opacity: 1 }}
                              transition={{ delay: 0.1 }}
                            >
                              <div className="size-6">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z" fill="#E53935"/>
                                </svg>
                              </div>
                            </motion.div>
                            <div className="agent-message-body flex flex-col max-w-full sm:max-w-[598px]">
                              <motion.div
                                className="bg-[#fff0f0] p-4 rounded-tr-lg rounded-br-lg rounded-bl-lg shadow-[0px_1px_6px_0px_rgba(229,57,53,0.3)] message-error-bubble break-words max-w-full border border-[#ffcdd2]"
                                initial={{ x: -20, opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                transition={{ type: "spring", stiffness: 500, damping: 30 }}
                              >
                                <div className="text-[#E53935] text-[16px]">
                                  <p>{message.content}</p>
                                </div>
                                <MessageActionBar
                                  message={message}
                                  onCopy={handleCopyMessage}
                                  onToggleReaction={handleToggleReaction}
                                  onDelete={handleDeleteMessage}
                                  loading={actionState}
                                  isCopied={isCopied}
                                  actionsDisabled={actionsDisabled}
                                />
                              </motion.div>
                              <motion.div
                                className="mt-2 ml-2"
                                initial={{ opacity: 0, y: 5 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.3 }}
                              >
                                <button
                                  className="text-[#165BFB] text-[14px] font-medium flex items-center gap-1.5"
                                  onClick={() => {
                                    const lastUserMessage = messages
                                      .filter(msg => msg.type === 'user')
                                      .pop();

                                    if (lastUserMessage) {
                                      handleUserResponse(
                                        lastUserMessage.content,
                                        lastUserMessage.vibe_block_design_wizard_answer
                                      ).catch(err =>
                                        console.error('Error retrying user response:', err)
                                      );
                                    }
                                  }}
                                >
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4C7.58 4 4.01 7.58 4.01 12C4.01 16.42 7.58 20 12 20C15.73 20 18.84 17.45 19.73 14H17.65C16.83 16.33 14.61 18 12 18C8.69 18 6 15.31 6 12C6 8.69 8.69 6 12 6C13.66 6 15.14 6.69 16.22 7.78L13 11H20V4L17.65 6.35Z" fill="#165BFB"/>
                                  </svg>
                                  Try again
                                </button>
                              </motion.div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col">
                      <div className="flex items-start gap-3">
                        <motion.div
                          className="agent-avatar rounded-full size-10 flex items-center justify-center relative overflow-hidden mt-1"
                          initial={{ scale: 0.8, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          transition={{ delay: 0.1 }}
                        >
                          <AssistantIcon messagesLength={messages.length} />
                        </motion.div>
                        <div className="agent-message-body flex flex-col max-w-full sm:max-w-[598px]">
                          <motion.div
                            className="bg-white p-4 rounded-tr-lg rounded-br-lg rounded-bl-lg shadow-[0px_1px_6px_0px_rgba(145,153,171,0.3)] message-ai-bubble break-words max-w-full"
                            initial={{ x: -20, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            transition={{ type: "spring", stiffness: 500, damping: 30 }}
                          >
                          {hasOfferPlanUpgrade && backendMessageId && (
                            <UpgradeRequiredNotice
                              showUpgradeButton={showOfferPlanUpgradeButton}
                              isUpgradeButtonLoading={isOfferPlanUpgradeButtonLoading}
                              onUpgradeClick={() => {
                                handleOpenOfferPlanUpgradeModal(backendMessageId).catch((error) => {
                                  console.error('Failed to open offer_plan_upgrade modal', error);
                                });
                              }}
                            />
                          )}
                          {messageDecisionItems.length > 0 && (
                            <div className="agent-context-card mb-3 rounded-2xl border border-[#ccdcff] bg-[#f7faff] p-3">
                              <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-[#4f6ea8]">
                                Decisions
                              </div>
                              <div className="space-y-2.5">
                                {messageDecisionItems.map((decision) => {
                                  const decisionDetails = decisionDetailsById[decision.id];
                                  const decisionMetadataItems = getDecisionMetadataItems(decisionDetails);
                                  const decisionState = decisionDetails?.state ?? decision.state;
                                  const upgradeRequired = getDecisionUpgradeRequired(decisionDetails);
                                  const hasUpgradeRequired = isUpgradeRequiredActive(upgradeRequired, decision.is_upgrade_required === true);
                                  const showUpgradeButton = shouldShowUpgradeButton(upgradeRequired);
                                  const isUpgradeButtonLoading = Boolean(upgradeRequiredOpenLoadingByDecisionId[decision.id]);
                                  const isAcceptLoading = Boolean(decisionActionLoading[decision.id]?.accept);
                                  const isDeclineLoading = Boolean(decisionActionLoading[decision.id]?.decline);
                                  const isUserInputLoading = Boolean(decisionActionLoading[decision.id]?.userInput);
                                  const isPublishLoading = Boolean(decisionActionLoading[decision.id]?.publish);
                                  const showHiddenForEndUserBanner = shouldShowHiddenForEndUserBanner(decision, decisionDetails);
                                  const autoApplyDecision = isAutoApplyDecision(decision, decisionDetails);
                                  const canAcceptOrDecline = !autoApplyDecision && (decisionState === 'created' || decisionState === 'userOwnValueSet') && !hasUpgradeRequired;
                                  const canSetUserInput = !autoApplyDecision && !isDecisionFinalState(decisionState) && Boolean(decisionDetails) && !hasUpgradeRequired;
                                  const websitePublishState = getDecisionWebsitePublishState(decisionDetails, decision);
                                  const showPublishButton = websitePublishState !== 'not_applicable';
                                  const isPublishDisabled = websitePublishState === 'published' || isPublishLoading;

                                  return (
                                    <div
                                      key={decision.id}
                                      className="rounded-xl border border-[#d9e6ff] bg-white px-3 py-2.5"
                                    >
                                      {showHiddenForEndUserBanner && (
                                        <div className="mb-2 rounded-lg border border-[#f5d78e] bg-[#fff8e6] px-3 py-2 text-[11px] font-semibold text-[#8a5a00]">
                                          Decision will be hidden from enduser
                                        </div>
                                      )}
                                      {hasUpgradeRequired && (
                                        <UpgradeRequiredNotice
                                          showUpgradeButton={showUpgradeButton}
                                          isUpgradeButtonLoading={isUpgradeButtonLoading}
                                          onUpgradeClick={() => {
                                            handleOpenUpgradeRequiredModal(decision.id).catch((error) => {
                                              console.error('Failed to open upgradeRequired modal', error);
                                            });
                                          }}
                                        />
                                      )}
                                      <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2">
                                        <span className="text-[13px] font-semibold text-[#27344a]">
                                          {formatDecisionLabel(decisionDetails)}
                                        </span>
                                        <span className="rounded-full bg-[#eef4ff] px-2 py-0.5 text-[11px] font-semibold text-[#2f5ca8]">
                                          {formatDecisionStateLabel(decisionState)}
                                        </span>
                                      </div>
                                      {decisionMetadataItems.length > 0 && (
                                        <div className="mb-2 space-y-1.5">
                                          {decisionMetadataItems.map((scopeItem) => (
                                            <DecisionScopeRow
                                              key={`${decision.id}-${scopeItem.key}`}
                                              label={scopeItem.label}
                                              value={scopeItem.value}
                                              copyKey={`${messageKey}-decision-${decision.id}-${scopeItem.key}`}
                                              copiedMentionKey={copiedMentionKey}
                                              onCopy={handleCopyMentionId}
                                            />
                                          ))}
                                        </div>
                                      )}
                                      <DecisionValueRow
                                        decisionId={decision.id}
                                        details={decisionDetails}
                                        decisionImagePreview={decisionImagePreviewById[decision.id]}
                                        onOpenDecisionImagePreview={handleOpenDecisionImagePreview}
                                      />
                                      {hasUpgradeRequired && (
                                        <div className="mb-2 text-[11px] font-medium text-[#b45309]">
                                          Upgrade is required before decision actions are available.
                                        </div>
                                      )}
                                      <div className="flex flex-wrap items-center gap-2">
                                        <button
                                          type="button"
                                          className={`rounded-full border px-3 py-1 text-[12px] font-semibold transition-colors ${
                                            canAcceptOrDecline
                                              ? 'border-[#b8d1ff] bg-[#eef4ff] text-[#2457cc] hover:border-[#8bb1ff]'
                                              : 'border-[#e5e7eb] bg-[#f9fafb] text-[#9ca3af] cursor-not-allowed'
                                          }`}
                                          disabled={!canAcceptOrDecline || isAcceptLoading}
                                          onClick={() => {
                                            handleDecisionAccept(decision.id).catch((error) => console.error('Failed to accept decision', error));
                                          }}
                                        >
                                          {getDecisionAcceptButtonLabel(decisionDetails, isAcceptLoading)}
                                        </button>
                                        <button
                                          type="button"
                                          className={`rounded-full border px-3 py-1 text-[12px] font-semibold transition-colors ${
                                            canAcceptOrDecline
                                              ? 'border-[#f5c2c7] bg-[#fff5f6] text-[#b4232f] hover:border-[#ee9aa3]'
                                              : 'border-[#e5e7eb] bg-[#f9fafb] text-[#9ca3af] cursor-not-allowed'
                                          }`}
                                          disabled={!canAcceptOrDecline || isDeclineLoading}
                                          onClick={() => {
                                            handleDecisionDecline(decision.id).catch((error) => console.error('Failed to decline decision', error));
                                          }}
                                        >
                                          {getDecisionDeclineButtonLabel(decisionDetails, isDeclineLoading)}
                                        </button>
                                        <button
                                          type="button"
                                          className={`rounded-full border px-3 py-1 text-[12px] font-semibold transition-colors ${
                                            canSetUserInput
                                              ? 'border-[#d7e3ff] bg-[#f8fbff] text-[#4f6ea8] hover:border-[#97b9ff]'
                                              : 'border-[#e5e7eb] bg-[#f9fafb] text-[#9ca3af] cursor-not-allowed'
                                          }`}
                                          disabled={!canSetUserInput || isUserInputLoading}
                                          onClick={() => {
                                            handleDecisionUserInput(decision, decisionDetails);
                                          }}
                                        >
                                          {isUserInputLoading ? 'Saving...' : 'User input'}
                                        </button>
                                        {showPublishButton && (
                                          <button
                                            type="button"
                                            className={`rounded-full border px-3 py-1 text-[12px] font-semibold transition-colors ${
                                              isPublishDisabled
                                                ? 'border-[#e5e7eb] bg-[#f9fafb] text-[#9ca3af] cursor-not-allowed'
                                                : 'border-[#bbf7d0] bg-[#f0fdf4] text-[#15803d] hover:border-[#86efac]'
                                            }`}
                                            disabled={isPublishDisabled}
                                            onClick={() => {
                                              handleDecisionPublish(decision.id, websitePublishState).catch((error) => {
                                                console.error('Failed to publish decision website state', error);
                                              });
                                            }}
                                          >
                                            {getDecisionPublishButtonLabel(websitePublishState, isPublishLoading)}
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          <div className="text-[#434343] text-[16px] chat-rich-text">
                            <p dangerouslySetInnerHTML={{ __html: htmlContent }}></p>
                          </div>
                          {message.vibe_block_design_wizard_question && (() => {
                            const wizard = message.vibe_block_design_wizard_question;
                            const progress = vibeBlockDesignWizardProgress.find((item) => item.wizardId === wizard.wizard_id);
                            const stepInfo = progress?.steps.find((step) => step.wizard.step === wizard.step);
                            const isStepAnswered = Boolean(stepInfo?.answer);
                            const isCurrentActiveStep = Boolean(
                              pendingVibeBlockDesignWizard
                              && pendingVibeBlockDesignWizard.wizard_id === wizard.wizard_id
                              && pendingVibeBlockDesignWizard.step === wizard.step
                            );
                            const isFinalStep = wizard.step === wizard.total_steps;
                            const showFullSummary = Boolean(progress?.isComplete && isFinalStep);
                            const completedSteps = showFullSummary
                              ? (progress?.steps.flatMap((step) => (
                                  step.answer ? [{ wizard: step.wizard, answer: step.answer }] : []
                                )) || [])
                              : (progress?.steps.flatMap((step) => (
                                  step.answer && step.wizard.step < wizard.step
                                    ? [{ wizard: step.wizard, answer: step.answer }]
                                    : []
                                )) || []);

                            return (
                              <VibeBlockDesignWizardCard
                                wizard={wizard}
                                answer={stepInfo?.answer}
                                completedSteps={completedSteps}
                                disabled={isStepAnswered || !isCurrentActiveStep || isLoading || isVibeBlockDesignWizardAdvancing}
                                isAdvancing={isCurrentActiveStep && isVibeBlockDesignWizardAdvancing}
                                language={selectedLanguage}
                                onSubmit={handleVibeBlockDesignWizardAnswer}
                              />
                            );
                          })()}
                          <MessageActionBar
                            message={message}
                            onCopy={handleCopyMessage}
                            onToggleReaction={handleToggleReaction}
                            onDelete={handleDeleteMessage}
                            loading={actionState}
                            isCopied={isCopied}
                            actionsDisabled={actionsDisabled || isVibeBlockDesignWizardPending}
                          />
                          </motion.div>

                          {message.id === "7" && showCompletionButtons && (
                            <motion.div
                              className="mt-4 flex flex-wrap gap-3"
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.5 }}
                            >
                              <motion.button
                                className="bg-[#165bfb] text-white rounded-[32px] px-6 py-3 text-[14px] font-medium flex items-center justify-center completion-button-primary"
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: 0.6 }}
                                onClick={handleProceed}
                              >
                                Proceed
                              </motion.button>
                              <motion.button
                                className="bg-white border border-[#edf0f5] text-[#464c5c] rounded-[32px] px-6 py-3 text-[14px] font-medium flex items-center justify-center completion-button-secondary"
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: 0.7 }}
                                onClick={() => setCurrentStep(1)}
                              >
                                Continue briefing
                              </motion.button>
                            </motion.div>
                          )}

                          {message.type === 'help' && message.suggestions && (
                            <motion.div
                              className="mt-4 flex flex-wrap gap-2"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ delay: 0.3 }}
                            >
                              {message.suggestions.map((suggestion, idx) => (
                                <motion.div
                                  key={String(suggestion)}
                                  initial={{ opacity: 0, scale: 0.8 }}
                                  animate={{ opacity: 1, scale: 1 }}
                                  transition={{ delay: 0.3 + (idx * 0.1) }}
                                >
                                  <SuggestionButton
                                    text={suggestion}
                                    onClick={() => handleHelpSuggestionClick(suggestion)}
                                  />
                                </motion.div>
                              ))}
                            </motion.div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </AnimatePresence>

          {/* Spacer element to maintain scroll distance from input */}
          <div className="h-4" ref={messagesEndRef}></div>
        </div>
      </div>

      {/* Fixed Input Area with Buffer Space and Gradient Fadeout */}
      <div className="agent-composer-area absolute bottom-0 left-0 right-0 pointer-events-none">
        {/* Gradient fadeout at the bottom to visually indicate messages approaching input */}
        <div className={`agent-composer-fade absolute bottom-0 left-0 right-0 h-[120px] bg-gradient-to-t from-white via-white to-transparent z-10 ${isMobile ? 'mobile-fadeout' : ''}`}></div>

        {/* Input Component */}
        <div className={`agent-composer-position relative mx-auto bottom-[52px] w-full max-w-[680px] z-20 pointer-events-auto ${isMobile ? 'mobile-input-container' : ''}`}>
          {isSiteBrief && isChatFinished && !isLoading && (
            <p className="mb-3 rounded-xl bg-[#f6f8ff] px-4 py-3 text-sm text-[#4b5a73]" role="status">
              {!siteBrief ? 'Loading site brief…' : siteBrief.active_task_id
                ? 'A site brief task is running. Chat will resume when it finishes.'
                : 'Chat is available while gathering requirements. Open Site Briefs to change the stage.'}
            </p>
          )}
          <AnimatePresence>
            {selectedImages.length > 0 && (
              <motion.div
                className="mb-3 w-full"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
              >
                <div className="agent-context-card rounded-2xl border border-[#ccdcff] bg-[#f7faff] p-3">
                  <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-[#4f6ea8]">
                    Images to upload
                  </div>
                  <div className="mb-2 text-[10px] font-medium text-[#5f6f89]">
                    Max size per image: {MAX_IMAGE_UPLOAD_SIZE_LABEL}
                  </div>
                  <div className="flex flex-wrap gap-2.5">
                    {selectedImages.map((img, index) => (
                      <div key={`${img.url}-${index}`} className="relative group rounded-xl border border-[#d9e6ff] bg-white p-1.5">
                        <button
                          type="button"
                          onClick={() => handleOpenImagePreview(img)}
                          className="text-left"
                          aria-label={`Open preview for ${img.name || `image ${index + 1}`}`}
                        >
                          <img
                            src={img.url}
                            alt={img.name || `Image ${index + 1}`}
                            className="h-20 w-20 rounded-lg object-cover"
                          />
                        </button>
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleRemoveImage(index);
                          }}
                          className="absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white opacity-0 transition-opacity group-hover:opacity-100"
                          aria-label={`Remove ${img.name || `image ${index + 1}`}`}
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="currentColor"/>
                          </svg>
                        </button>
                        <div className="mt-1 max-w-[80px] truncate text-[10px] font-medium text-[#5f6f89]">
                          {img.name || `Image ${index + 1}`}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div
            className={`agent-mention-controls mb-3 flex-wrap items-center gap-2 ${isSiteBrief ? 'hidden' : 'flex'} ${isVibeBlockDesignWizardPending ? 'opacity-60' : ''}`}
            ref={mentionControlsRef}
          >
            <div className="relative">
              <button
                type="button"
                className={`h-9 rounded-full border px-4 text-[13px] font-semibold transition-colors ${
                  isMentionPickerOpen
                    ? 'border-[#93b5ff] bg-[#eef4ff] text-[#165BFB]'
                    : 'border-[#dbe7ff] bg-white text-[#4b5a73] hover:border-[#b8d1ff]'
                } ${isChatFinished || isLoading || isVibeBlockDesignWizardPending ? 'cursor-not-allowed opacity-60' : ''}`}
                onClick={handleMentionPickerToggle}
                disabled={isChatFinished || isLoading || isVibeBlockDesignWizardPending}
              >
                Mention website
              </button>

              <AnimatePresence>
                {isMentionPickerOpen && (
                  <motion.div
                    className="agent-mention-menu absolute bottom-[46px] left-0 z-30 w-[320px] rounded-2xl border border-[#dbe7ff] bg-white p-2 shadow-[0px_12px_28px_rgba(22,91,251,0.12)]"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.15 }}
                  >
                    <div className="px-2 pb-2 text-[12px] font-semibold text-[#5f6f89]">
                      Select one website
                    </div>
                    {isMentionPickerLoading && (
                      <div className="px-2 py-3 text-[13px] text-[#6B7280]">Loading websites...</div>
                    )}
                    {!isMentionPickerLoading && mentionPickerError && (
                      <div className="px-2 py-3 text-[13px] text-[#dc2626]">{mentionPickerError}</div>
                    )}
                    {!isMentionPickerLoading && !mentionPickerError && mentionWebsites.length === 0 && (
                      <div className="px-2 py-3 text-[13px] text-[#6B7280]">No websites found</div>
                    )}
                    {!isMentionPickerLoading && !mentionPickerError && mentionWebsites.length > 0 && (
                      <div className="max-h-56 overflow-y-auto custom-scrollbar">
                        {mentionWebsites.map((website) => (
                          <button
                            key={website.id}
                            type="button"
                            className={`w-full rounded-xl px-3 py-2 text-left transition-colors hover:bg-[#f5f8ff] ${
                              selectedMentionWebsite?.id === website.id ? 'bg-[#eef4ff]' : ''
                            }`}
                            onClick={() => handleSelectMentionWebsite(website)}
                          >
                            <div className="truncate text-[13px] font-semibold text-[#27344a]">
                              {website.name || getWebsiteDomainLabel(website)}
                            </div>
                            <div className="truncate text-[12px] text-[#6b7280]">
                              {getWebsiteDomainLabel(website)}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="relative">
              <button
                type="button"
                className={`h-9 rounded-full border px-4 text-[13px] font-semibold transition-colors ${
                  isPageMentionPickerOpen
                    ? 'border-[#93b5ff] bg-[#eef4ff] text-[#165BFB]'
                    : 'border-[#dbe7ff] bg-white text-[#4b5a73] hover:border-[#b8d1ff]'
                } ${isChatFinished || isLoading || isVibeBlockDesignWizardPending || !selectedMentionWebsite ? 'cursor-not-allowed opacity-60' : ''}`}
                onClick={handlePageMentionPickerToggle}
                disabled={isChatFinished || isLoading || isVibeBlockDesignWizardPending || !selectedMentionWebsite}
              >
                Mention page
              </button>

              <AnimatePresence>
                {isPageMentionPickerOpen && (
                  <motion.div
                    className="agent-mention-menu absolute bottom-[46px] left-0 z-30 w-[340px] rounded-2xl border border-[#dbe7ff] bg-white p-2 shadow-[0px_12px_28px_rgba(22,91,251,0.12)]"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.15 }}
                  >
                    <div className="px-2 pb-2 text-[12px] font-semibold text-[#5f6f89]">
                      Select up to 10 pages
                    </div>
                    {isPageMentionPickerLoading && (
                      <div className="px-2 py-3 text-[13px] text-[#6B7280]">Loading pages...</div>
                    )}
                    {!isPageMentionPickerLoading && pageMentionPickerError && (
                      <div className="px-2 py-3 text-[13px] text-[#dc2626]">{pageMentionPickerError}</div>
                    )}
                    {!isPageMentionPickerLoading && !pageMentionPickerError && mentionPages.length === 0 && (
                      <div className="px-2 py-3 text-[13px] text-[#6B7280]">No pages found</div>
                    )}
                    {!isPageMentionPickerLoading && !pageMentionPickerError && mentionPages.length > 0 && (
                      <div className="max-h-56 overflow-y-auto custom-scrollbar">
                        {mentionPages.map((page) => {
                          const isSelected = selectedMentionPages.some((selectedPage) => selectedPage.id === page.id);
                          const isLimitReached = selectedMentionPages.length >= 10 && !isSelected;

                          return (
                            <button
                              key={page.id}
                              type="button"
                              className={`w-full rounded-xl px-3 py-2 text-left transition-colors ${
                                isSelected ? 'bg-[#eef4ff]' : 'hover:bg-[#f5f8ff]'
                              } ${isLimitReached ? 'cursor-not-allowed opacity-60' : ''}`}
                              onClick={() => {
                                if (isLimitReached) {
                                  return;
                                }
                                handleToggleMentionPage(page);
                              }}
                              disabled={isLimitReached}
                            >
                              <div className="flex items-center justify-between gap-2">
                                <div className="truncate text-[13px] font-semibold text-[#27344a]">
                                  {page.label || page.name || 'Untitled page'}
                                </div>
                                {isSelected && (
                                  <span className="text-[12px] font-semibold text-[#165BFB]">Selected</span>
                                )}
                              </div>
                              <div className="truncate text-[12px] text-[#6b7280]">
                                <span>{page.metadata?.slug || 'No slug'}</span>
                                <span className="mx-1 text-[#a3b5d9]">•</span>
                                <span>{page.is_home_page ? 'Home page' : 'Page'}</span>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {pageMentionSelectionError && (
                      <div className="px-2 pt-2 text-[12px] text-[#dc2626]">{pageMentionSelectionError}</div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="relative">
              <button
                type="button"
                className={`h-9 rounded-full border px-4 text-[13px] font-semibold transition-colors ${
                  isBlockMentionPickerOpen
                    ? 'border-[#93b5ff] bg-[#eef4ff] text-[#165BFB]'
                    : 'border-[#dbe7ff] bg-white text-[#4b5a73] hover:border-[#b8d1ff]'
                } ${isChatFinished || isLoading || isVibeBlockDesignWizardPending || !selectedMentionWebsite || selectedMentionPages.length === 0 ? 'cursor-not-allowed opacity-60' : ''}`}
                onClick={handleBlockMentionPickerToggle}
                disabled={isChatFinished || isLoading || isVibeBlockDesignWizardPending || !selectedMentionWebsite || selectedMentionPages.length === 0}
              >
                Mention block
              </button>

              <AnimatePresence>
                {isBlockMentionPickerOpen && (
                  <motion.div
                    className="agent-mention-menu absolute bottom-[46px] left-0 z-30 w-[340px] rounded-2xl border border-[#dbe7ff] bg-white p-2 shadow-[0px_12px_28px_rgba(22,91,251,0.12)]"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.15 }}
                  >
                    <div className="px-2 pb-2 text-[12px] font-semibold text-[#5f6f89]">
                      Select one block
                    </div>
                    {isBlockMentionPickerLoading && (
                      <div className="px-2 py-3 text-[13px] text-[#6B7280]">Loading blocks...</div>
                    )}
                    {!isBlockMentionPickerLoading && blockMentionPickerError && (
                      <div className="px-2 py-3 text-[13px] text-[#dc2626]">{blockMentionPickerError}</div>
                    )}
                    {!isBlockMentionPickerLoading && !blockMentionPickerError && mentionBlocks.length === 0 && (
                      <div className="px-2 py-3 text-[13px] text-[#6B7280]">No blocks found</div>
                    )}
                    {!isBlockMentionPickerLoading && !blockMentionPickerError && mentionBlocks.length > 0 && (
                      <div className="max-h-56 overflow-y-auto custom-scrollbar">
                        {mentionBlocks.map((block) => {
                          const isSelected = selectedMentionBlock?.id === block.id;
                          const displayName = block.label || block.name || 'Untitled block';

                          return (
                            <button
                              key={block.id}
                              type="button"
                              className={`w-full rounded-xl px-3 py-2 text-left transition-colors ${
                                isSelected ? 'bg-[#eef4ff]' : 'hover:bg-[#f5f8ff]'
                              }`}
                              onClick={() => handleSelectMentionBlock(block)}
                            >
                              <div className="flex items-center justify-between gap-2">
                                <div className="truncate text-[13px] font-semibold text-[#27344a]">
                                  {displayName}
                                </div>
                                {isSelected && (
                                  <span className="text-[12px] font-semibold text-[#165BFB]">Selected</span>
                                )}
                              </div>
                              <div className="truncate text-[12px] text-[#6b7280]">
                                {block.id}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="relative">
              <button
                type="button"
                className={`h-9 rounded-full border px-4 text-[13px] font-semibold transition-colors ${
                  isElementMentionPickerOpen
                    ? 'border-[#93b5ff] bg-[#eef4ff] text-[#165BFB]'
                    : 'border-[#dbe7ff] bg-white text-[#4b5a73] hover:border-[#b8d1ff]'
                } ${isChatFinished || isLoading || isVibeBlockDesignWizardPending || !selectedMentionBlock ? 'cursor-not-allowed opacity-60' : ''}`}
                onClick={handleElementMentionPickerToggle}
                disabled={isChatFinished || isLoading || isVibeBlockDesignWizardPending || !selectedMentionBlock}
              >
                Mention element
              </button>

              <AnimatePresence>
                {isElementMentionPickerOpen && (
                  <motion.div
                    className="agent-mention-menu absolute bottom-[46px] left-0 z-30 w-[340px] rounded-2xl border border-[#dbe7ff] bg-white p-2 shadow-[0px_12px_28px_rgba(22,91,251,0.12)]"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.15 }}
                  >
                    <div className="px-2 pb-2 text-[12px] font-semibold text-[#5f6f89]">
                      Select up to 10 elements
                    </div>
                    {isElementMentionPickerLoading && (
                      <div className="px-2 py-3 text-[13px] text-[#6B7280]">Loading elements...</div>
                    )}
                    {!isElementMentionPickerLoading && elementMentionPickerError && (
                      <div className="px-2 py-3 text-[13px] text-[#dc2626]">{elementMentionPickerError}</div>
                    )}
                    {!isElementMentionPickerLoading && !elementMentionPickerError && mentionElements.length === 0 && (
                      <div className="px-2 py-3 text-[13px] text-[#6B7280]">No elements found</div>
                    )}
                    {!isElementMentionPickerLoading && !elementMentionPickerError && mentionElements.length > 0 && (
                      <div className="max-h-56 overflow-y-auto custom-scrollbar">
                        {mentionElements.map((element) => {
                          const isSelected = selectedMentionElements.some((selectedElement) => selectedElement.id === element.id);
                          const isLimitReached = selectedMentionElements.length >= 10 && !isSelected;
                          const displayName = element.label || element.name || 'Untitled element';

                          return (
                            <button
                              key={element.id}
                              type="button"
                              className={`w-full rounded-xl px-3 py-2 text-left transition-colors ${
                                isSelected ? 'bg-[#eef4ff]' : 'hover:bg-[#f5f8ff]'
                              } ${isLimitReached ? 'cursor-not-allowed opacity-60' : ''}`}
                              onClick={() => {
                                if (isLimitReached) {
                                  return;
                                }
                                handleToggleMentionElement(element);
                              }}
                              disabled={isLimitReached}
                            >
                              <div className="flex items-center justify-between gap-2">
                                <div className="truncate text-[13px] font-semibold text-[#27344a]">
                                  {displayName}
                                </div>
                                {isSelected && (
                                  <span className="text-[12px] font-semibold text-[#165BFB]">Selected</span>
                                )}
                              </div>
                              <div className="truncate text-[12px] text-[#6b7280]">
                                {element.id}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {elementMentionSelectionError && (
                      <div className="px-2 pt-2 text-[12px] text-[#dc2626]">{elementMentionSelectionError}</div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {selectedMentionWebsite && (
              <div className="inline-flex items-center gap-2 rounded-full border border-[#b8d1ff] bg-[#eef4ff] px-3 py-1.5 text-[13px] font-semibold text-[#2457cc]">
                <span className="max-w-[300px] truncate">{selectedMentionWebsite.name || getWebsiteDomainLabel(selectedMentionWebsite)}</span>
                <button
                  type="button"
                  onClick={handleClearMentionWebsite}
                  className="rounded-full p-0.5 text-[#5f85df] transition-colors hover:bg-[#dfe9ff] hover:text-[#165BFB]"
                  aria-label="Clear selected website mention"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </button>
              </div>
            )}

            {selectedMentionPages.length > 0 && selectedMentionPages.map((page) => (
              <div
                key={page.id}
                className="inline-flex items-center gap-2 rounded-full border border-[#d8e6ff] bg-[#f4f8ff] px-3 py-1.5 text-[13px] font-semibold text-[#2f5ca8]"
              >
                <span className="inline-flex size-4 items-center justify-center rounded bg-[#e4eeff] text-[#2f5ca8]">
                  <MentionTypeIcon entityType="Page" isHomePage={page.is_home_page} />
                </span>
                <span className="max-w-[240px] truncate">{getPageMentionLabel(page)}</span>
                <button
                  type="button"
                  onClick={() => handleRemoveMentionPage(page.id)}
                  className="rounded-full p-0.5 text-[#6f93d9] transition-colors hover:bg-[#dfe9ff] hover:text-[#165BFB]"
                  aria-label={`Clear selected page mention ${getPageMentionLabel(page)}`}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </button>
              </div>
            ))}

            {selectedMentionBlock && (
              <div className="inline-flex items-center gap-2 rounded-full border border-[#d8e6ff] bg-[#f4f8ff] px-3 py-1.5 text-[13px] font-semibold text-[#2f5ca8]">
                <span className="inline-flex size-4 items-center justify-center rounded bg-[#e4eeff] text-[#2f5ca8]">
                  <MentionTypeIcon entityType="Block" />
                </span>
                <span className="max-w-[240px] truncate">{getBlockMentionLabel(selectedMentionBlock)}</span>
                <button
                  type="button"
                  onClick={handleClearMentionBlock}
                  className="rounded-full p-0.5 text-[#6f93d9] transition-colors hover:bg-[#dfe9ff] hover:text-[#165BFB]"
                  aria-label={`Clear selected block mention ${getBlockMentionLabel(selectedMentionBlock)}`}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </button>
              </div>
            )}

            {selectedMentionElements.length > 0 && selectedMentionElements.map((element) => (
              <div
                key={element.id}
                className="inline-flex items-center gap-2 rounded-full border border-[#d8e6ff] bg-[#f4f8ff] px-3 py-1.5 text-[13px] font-semibold text-[#2f5ca8]"
              >
                <span className="inline-flex size-4 items-center justify-center rounded bg-[#e4eeff] text-[#2f5ca8]">
                  <MentionTypeIcon entityType="Element" />
                </span>
                <span className="max-w-[240px] truncate">{getElementMentionLabel(element)}</span>
                <button
                  type="button"
                  onClick={() => handleRemoveMentionElement(element.id)}
                  className="rounded-full p-0.5 text-[#6f93d9] transition-colors hover:bg-[#dfe9ff] hover:text-[#165BFB]"
                  aria-label={`Clear selected element mention ${getElementMentionLabel(element)}`}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </button>
              </div>
            ))}
          </div>

          <motion.div
            className="w-full flex items-end gap-4"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3, type: "spring", stiffness: 400, damping: 25 }}
          >
            {/* Main Input Container */}
            <motion.div
              className="agent-composer bg-[#ffffff] relative rounded-2xl flex-grow shadow-lg"
              style={{ height: chatInputHeight }}
              whileHover={{ boxShadow: "0px 4px 14px rgba(56, 76, 116, 0.04)" }}
              transition={{ duration: 0.2 }}
              onDragOver={(event) => {
                if (event.dataTransfer.types.includes('Files')) {
                  event.preventDefault();
                }
              }}
              onDrop={async (event) => {
                 const droppedFiles = event.dataTransfer.files;
                 if (droppedFiles && droppedFiles.length > 0) {
                   event.preventDefault();
                   if (isChatFinished || isVibeBlockDesignWizardPending) return;
                   const imageFiles = Array.from(droppedFiles).filter(isImageFile);
                   if (imageFiles.length > 0) {
                     await processImageFiles(imageFiles);
                   }
                 }
              }}
            >
              <div className="overflow-clip relative w-full" style={{ height: chatInputHeight }}>
                {/* Placeholder text */}
                {!isVibeBlockDesignWizardPending && !userInput && selectedImages.length === 0 && (
                  <motion.div
                    className={`agent-composer-placeholder absolute top-1/2 -translate-y-1/2 text-[#a4a8b3] text-[16px] ${isMobile ? 'left-[76px] text-[14px]' : 'left-[92px]'}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.1 }}
                  >
                    <p>{isSiteBrief ? 'Discuss your website brief…' : isMobile ? 'Write or attach images...' : 'Write an answer or attach images...'}</p>
                  </motion.div>
                )}

                {/* Input field */}
                <textarea
                  ref={inputRef}
                  value={userInput}
                  onChange={(e) => {
                    if (isChatFinished || isVibeBlockDesignWizardPending) return;
                    setUserInput(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (isChatFinished || isLoading || isVibeBlockDesignWizardPending) return;
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      if (userInput || selectedImages.length > 0) {
                        handleUserResponse().catch(err => console.error('Error handling user response:', err));
                      }
                    }
                  }}
                  onPaste={async (event) => {
                    if (isChatFinished || isVibeBlockDesignWizardPending) return;
                    const imageFiles = extractImagesFromClipboard(event.clipboardData);
                    if (imageFiles.length > 0) {
                      event.preventDefault();
                      await processImageFiles(imageFiles);
                    }
                  }}
                  rows={1}
                  className={`agent-composer-input absolute left-0 right-0 top-0 resize-none overflow-y-auto bg-transparent border-none outline-none text-[16px] leading-6 text-[#333333] py-4 ${isMobile ? 'px-16 text-[14px]' : 'px-20'} ${isChatFinished || isVibeBlockDesignWizardPending ? 'opacity-50 cursor-not-allowed' : ''}`}
                  style={{ height: chatInputHeight }}
                  disabled={isChatFinished || isVibeBlockDesignWizardPending}
                  aria-disabled={isChatFinished || isVibeBlockDesignWizardPending}
                  tabIndex={isChatFinished || isVibeBlockDesignWizardPending ? -1 : 0}
                  aria-label="Chat input"
                />

                {/* Hidden image input */}
                <input
                  type="file"
                  ref={imageInputRef}
                  onChange={handleImageSelect}
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  multiple
                  style={{ display: 'none' }}
                />

                {/* Blue Image attachment button */}
                <div className={`absolute left-4 top-1/2 -translate-y-1/2 ${isSiteBrief ? 'hidden' : ''} ${isMobile ? 'size-8' : 'size-9'}`}>
                  <motion.button
                    className={`agent-attach-button size-full flex items-center justify-center rounded-[27.4286px] cursor-pointer overflow-hidden bg-[#4b81ff] hover:bg-[#3a70ff] active:bg-[#2c65ff] ${isChatFinished || isLoading || isVibeBlockDesignWizardPending ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''}`}
                    onClick={isChatFinished || isLoading || isVibeBlockDesignWizardPending ? undefined : handleImageClick}
                    whileHover={isChatFinished || isLoading || isVibeBlockDesignWizardPending ? {} : { scale: 1.05 }}
                    whileTap={isChatFinished || isLoading || isVibeBlockDesignWizardPending ? {} : { scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                    aria-label="Attach image"
                    disabled={isChatFinished || isLoading || isVibeBlockDesignWizardPending}
                    aria-disabled={isChatFinished || isLoading || isVibeBlockDesignWizardPending}
                    tabIndex={isChatFinished || isLoading || isVibeBlockDesignWizardPending ? -1 : 0}
                  >
                    {/* Image icon */}
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M21 19V5C21 3.9 20.1 3 19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19ZM8.5 13.5L11 16.51L14.5 12L19 18H5L8.5 13.5Z" fill="white"/>
                    </svg>
                  </motion.button>
                </div>
              </div>
              <div className="agent-composer-outline absolute border-2 border-[#ecf2ff] border-solid inset-[-2px] pointer-events-none rounded-[18px]" />
            </motion.div>

            {/* Separate Send Button */}
            <motion.div
              className={`agent-send-button h-16 relative rounded-2xl cursor-pointer flex items-center justify-center send-button ${isMobile ? 'w-14' : 'w-16'} ${sendButtonAnimation ? 'send-button-press' : ''} ${userInput || selectedImages.length > 0 ? 'agent-send-ready bg-[#dee8ff]' : 'bg-[#f6f8ff]'} ${isLoading ? 'opacity-60 cursor-wait' : ''} ${isChatFinished || isVibeBlockDesignWizardPending ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''}`}
              onClick={() => {
                if (isChatFinished || isLoading || isVibeBlockDesignWizardPending) return;
                if (userInput || selectedImages.length > 0) {
                  handleUserResponse().catch(err => console.error('Error handling user response:', err));
                }
              }}
              whileHover={isChatFinished || isLoading || isVibeBlockDesignWizardPending ? {} : (userInput || selectedImages.length > 0 ? { scale: 1.03, boxShadow: "0px 4px 10px rgba(22, 91, 251, 0.1)" } : {})}
              whileTap={isChatFinished || isLoading || isVibeBlockDesignWizardPending ? {} : (userInput || selectedImages.length > 0 ? { scale: 0.95 } : {})}
              transition={{ duration: 0.2 }}
              aria-disabled={isChatFinished || isLoading || isVibeBlockDesignWizardPending}
              tabIndex={isChatFinished || isLoading || isVibeBlockDesignWizardPending ? -1 : 0}
            >
              <div className={`flex items-center justify-center ${isMobile ? 'size-6' : 'size-8'}`}>
                {isLoading ? (
                  <svg className="animate-spin" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" stroke="#A4A8B3" strokeWidth="2.5" />
                    <path d="M12 2C14.717 2 17.337 3.06458 19.2695 4.95867C21.2021 6.85276 22.3362 9.42975 22.3928 12.1455C22.4493 14.8612 21.4241 17.4813 19.5722 19.4518" stroke="#6B7280" strokeWidth="3" strokeLinecap="round" />
                  </svg>
                ) : (
                  <svg className={`${isMobile ? 'size-5' : 'size-6'}`} viewBox="0 0 24 24" fill="none">
                    <path
                      d={svgInputPaths.p5b88880}
                      fill={userInput || selectedImages.length > 0 ? "#165BFB" : "#A4A8B3"}
                    />
                  </svg>
                )}
              </div>
              <div className="agent-composer-outline absolute border-2 border-[#dee8ff] border-solid inset-[-2px] pointer-events-none rounded-[18px]" />
            </motion.div>
          </motion.div>
        </div>

        {/* Suggestion buttons - positioned above the input */}
        <AnimatePresence>
          {!isSiteBrief && !isVibeBlockDesignWizardPending && showSuggestions && currentStep < chatFlow.length && chatFlow[currentStep].suggestions && (
            <div className="absolute bottom-0 left-0 right-0 flex justify-center w-full pointer-events-none">
              <motion.div
                className={`absolute ${isMobile ? 'bottom-[120px]' : 'bottom-[140px]'} flex gap-2 flex-wrap justify-center z-20 max-w-[680px] w-full pointer-events-auto px-4`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                transition={{ delay: 0.4 }}
              >
                {chatFlow[currentStep].suggestions.map((suggestion, index) => (
                  <motion.button
                    key={String(suggestion)}
                    className="bg-[#165bfb] text-white rounded-[64px] px-3.5 py-2 text-[14px] suggestion-button"
                    onClick={() => handleUserResponse(suggestion).catch(err => console.error('Error handling suggestion:', err))}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.4 + (index * 0.1) }}
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {suggestion}
                  </motion.button>
                ))}
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {decisionUserInputModal && (
          <motion.div
            className="agent-overlay fixed inset-0 z-[95] flex items-center justify-center bg-[#0f172acc] p-4 backdrop-blur-sm"
            onClick={handleCloseDecisionUserInputModal}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.form
              className="agent-dialog w-full max-w-xl rounded-2xl border border-[#ccdcff] bg-[#f7faff] p-5 shadow-[0px_24px_56px_rgba(15,23,42,0.35)]"
              onClick={(event) => event.stopPropagation()}
              onSubmit={(event) => {
                event.preventDefault();
                handleDecisionUserInputSubmit().catch((error) => console.error('Failed to submit decision user input', error));
              }}
              initial={{ opacity: 0, y: 16, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.96 }}
              transition={{ duration: 0.2 }}
              role="dialog"
              aria-modal="true"
              aria-labelledby="decision-user-input-title"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h2 id="decision-user-input-title" className="text-[18px] font-semibold text-[#1f2a44]">
                    Set User Value
                  </h2>
                  <p className="mt-1 text-[13px] text-[#4f6ea8]">
                    {decisionUserInputModal.decisionTypeLabel}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleCloseDecisionUserInputModal}
                  className="shrink-0 rounded-lg border border-[#d7e3ff] bg-white p-1.5 text-[#4f6ea8] transition-colors hover:border-[#97b9ff] hover:text-[#165BFB]"
                  aria-label="Close user input popup"
                >
                  <svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <path d="M18 6 6 18M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="mt-4 rounded-xl border border-[#dbe7ff] bg-white p-3">
                <div className="mb-3 inline-flex rounded-full border border-[#dbe7ff] bg-[#f4f8ff] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.06em] text-[#2f5ca8]">
                  Value type: {decisionUserInputModal.valueType}
                </div>

                {decisionUserInputModal.valueType === 'boolean' && (
                  <div>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      <button
                        type="button"
                        onClick={() => {
                          setDecisionUserInputModal((prev) => prev
                            ? { ...prev, booleanValue: true, error: null }
                            : prev);
                        }}
                        className={`rounded-xl border px-3 py-3 text-left transition-colors ${
                          decisionUserInputModal.booleanValue
                            ? 'border-[#7bb29c] bg-[#eafaf2] text-[#166534]'
                            : 'border-[#dbe7ff] bg-[#f8fbff] text-[#334155] hover:border-[#97b9ff]'
                        }`}
                      >
                        <div className="text-[12px] font-semibold uppercase tracking-[0.06em]">
                          Turn on
                        </div>
                        <div className="mt-1 text-[12px] opacity-85">
                          Submit true
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setDecisionUserInputModal((prev) => prev
                            ? { ...prev, booleanValue: false, error: null }
                            : prev);
                        }}
                        className={`rounded-xl border px-3 py-3 text-left transition-colors ${
                          !decisionUserInputModal.booleanValue
                            ? 'border-[#e8a4ab] bg-[#fff5f6] text-[#b4232f]'
                            : 'border-[#dbe7ff] bg-[#f8fbff] text-[#334155] hover:border-[#97b9ff]'
                        }`}
                      >
                        <div className="text-[12px] font-semibold uppercase tracking-[0.06em]">
                          Turn off
                        </div>
                        <div className="mt-1 text-[12px] opacity-85">
                          Submit false
                        </div>
                      </button>
                    </div>
                  </div>
                )}

                {decisionUserInputModal.valueType === 'number' && (
                  <div>
                    <label htmlFor="decision-user-input-number" className="mb-1 block text-[12px] font-semibold uppercase tracking-[0.06em] text-[#4f6ea8]">
                      Numeric value
                    </label>
                    <input
                      id="decision-user-input-number"
                      type="number"
                      step="any"
                      value={decisionUserInputModal.numberValue}
                      onChange={(event) => {
                        setDecisionUserInputModal((prev) => prev
                          ? { ...prev, numberValue: event.target.value, error: null }
                          : prev);
                      }}
                      className="w-full rounded-xl border border-[#d7e3ff] bg-white px-3 py-2 text-[14px] text-[#1f2a44] outline-none transition-colors focus:border-[#7aa8ff]"
                      autoFocus
                    />
                  </div>
                )}

                {decisionUserInputModal.valueType === 'shortText' && (
                  <div>
                    <label htmlFor="decision-user-input-short-text" className="mb-1 block text-[12px] font-semibold uppercase tracking-[0.06em] text-[#4f6ea8]">
                      Short text value
                    </label>
                    <input
                      id="decision-user-input-short-text"
                      type="text"
                      maxLength={MAX_DECISION_SHORT_TEXT_LENGTH}
                      value={decisionUserInputModal.shortTextValue}
                      onChange={(event) => {
                        setDecisionUserInputModal((prev) => prev
                          ? { ...prev, shortTextValue: event.target.value, error: null }
                          : prev);
                      }}
                      className="w-full rounded-xl border border-[#d7e3ff] bg-white px-3 py-2 text-[14px] text-[#1f2a44] outline-none transition-colors focus:border-[#7aa8ff]"
                      autoFocus
                    />
                    <div className="mt-1 text-right text-[11px] text-[#6b7c99]">
                      {decisionUserInputModal.shortTextValue.length}/{MAX_DECISION_SHORT_TEXT_LENGTH}
                    </div>
                  </div>
                )}

                {decisionUserInputModal.valueType === 'longText' && (
                  <div>
                    <label htmlFor="decision-user-input-long-text" className="mb-1 block text-[12px] font-semibold uppercase tracking-[0.06em] text-[#4f6ea8]">
                      Long text value
                    </label>
                    <textarea
                      id="decision-user-input-long-text"
                      rows={7}
                      maxLength={MAX_DECISION_LONG_TEXT_LENGTH}
                      value={decisionUserInputModal.longTextValue}
                      onChange={(event) => {
                        setDecisionUserInputModal((prev) => prev
                          ? { ...prev, longTextValue: event.target.value, error: null }
                          : prev);
                      }}
                      className="w-full resize-y rounded-xl border border-[#d7e3ff] bg-white px-3 py-2 text-[14px] leading-6 text-[#1f2a44] outline-none transition-colors focus:border-[#7aa8ff]"
                      autoFocus
                    />
                    <div className="mt-1 text-right text-[11px] text-[#6b7c99]">
                      {decisionUserInputModal.longTextValue.length}/{MAX_DECISION_LONG_TEXT_LENGTH}
                    </div>
                  </div>
                )}
              </div>

              {decisionUserInputModal.error && (
                <div className="mt-3 rounded-lg border border-[#f5c2c7] bg-[#fff5f6] px-3 py-2 text-[13px] text-[#b4232f]">
                  {decisionUserInputModal.error}
                </div>
              )}

              <div className="mt-4 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={handleCloseDecisionUserInputModal}
                  className="rounded-xl border border-[#d7e3ff] bg-white px-4 py-2 text-[13px] font-semibold text-[#4f6ea8] transition-colors hover:border-[#97b9ff] hover:text-[#165BFB]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isDecisionUserInputSubmitting}
                  className={`rounded-xl px-4 py-2 text-[13px] font-semibold text-white transition-colors ${
                    isDecisionUserInputSubmitting
                      ? 'cursor-wait bg-[#8cb0ff]'
                      : 'bg-[#165BFB] hover:bg-[#0f4fe0]'
                  }`}
                >
                  {isDecisionUserInputSubmitting ? 'Saving...' : 'Save value'}
                </button>
              </div>
            </motion.form>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {upgradeRequiredModal && (
          <motion.div
            className="agent-overlay fixed inset-0 z-[96] flex items-center justify-center bg-[#0f172acc] p-4 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="agent-dialog w-full max-w-lg rounded-2xl border border-[#f4dcb0] bg-[#fff8eb] p-5 shadow-[0px_24px_56px_rgba(15,23,42,0.35)]"
              onClick={(event) => event.stopPropagation()}
              initial={{ opacity: 0, y: 16, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.96 }}
              transition={{ duration: 0.2 }}
              role="dialog"
              aria-modal="true"
              aria-labelledby="upgrade-required-modal-title"
            >
              <h2 id="upgrade-required-modal-title" className="text-[18px] font-semibold text-[#1f2a44]">
                Upgrade to Pro
              </h2>
              <p className="mt-2 text-[14px] leading-6 text-[#475569]">
                Virtual Plans list modal. Please upgrade subscription on the Weblium&apos;s side.
              </p>

              {upgradeRequiredModal.error && (
                <div className="mt-3 rounded-lg border border-[#f5c2c7] bg-[#fff5f6] px-3 py-2 text-[13px] text-[#b4232f]">
                  {upgradeRequiredModal.error}
                </div>
              )}

              <div className="mt-5 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    handleUpgradeRequiredModalAction('cancel').catch((error) => {
                      console.error('Failed to submit upgradeRequired cancel action', error);
                    });
                  }}
                  disabled={upgradeRequiredModal.isSubmitting}
                  className={`rounded-xl border px-4 py-2 text-[13px] font-semibold transition-colors ${
                    upgradeRequiredModal.isSubmitting
                      ? 'cursor-wait border-[#e5e7eb] bg-[#f9fafb] text-[#9ca3af]'
                      : 'border-[#d7e3ff] bg-white text-[#4f6ea8] hover:border-[#97b9ff] hover:text-[#165BFB]'
                  }`}
                >
                  {upgradeRequiredModal.isSubmitting && upgradeRequiredModal.activeAction === 'cancel'
                    ? 'saving...'
                    : 'cancel'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    handleUpgradeRequiredModalAction('confirm').catch((error) => {
                      console.error('Failed to submit upgradeRequired confirm action', error);
                    });
                  }}
                  disabled={upgradeRequiredModal.isSubmitting}
                  className={`agent-upgrade-button rounded-xl px-4 py-2 text-[13px] font-semibold text-white transition-opacity ${
                    upgradeRequiredModal.isSubmitting
                      ? 'cursor-wait bg-[#f4b8ca]'
                      : 'bg-gradient-to-r from-[#eb6f95] to-[#f0b175] hover:opacity-95'
                  }`}
                >
                  {upgradeRequiredModal.isSubmitting && upgradeRequiredModal.activeAction === 'confirm'
                    ? 'saving...'
                    : 'confirm upgrade'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {offerPlanUpgradeModal && (
          <motion.div
            className="agent-overlay fixed inset-0 z-[96] flex items-center justify-center bg-[#0f172acc] p-4 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="agent-dialog w-full max-w-lg rounded-2xl border border-[#f4dcb0] bg-[#fff8eb] p-5 shadow-[0px_24px_56px_rgba(15,23,42,0.35)]"
              onClick={(event) => event.stopPropagation()}
              initial={{ opacity: 0, y: 16, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.96 }}
              transition={{ duration: 0.2 }}
              role="dialog"
              aria-modal="true"
              aria-labelledby="offer-plan-upgrade-modal-title"
            >
              <h2 id="offer-plan-upgrade-modal-title" className="text-[18px] font-semibold text-[#1f2a44]">
                Upgrade to Pro
              </h2>
              <p className="mt-2 text-[14px] leading-6 text-[#475569]">
                Virtual Plans list modal. Please upgrade subscription on the Weblium&apos;s side.
              </p>

              {offerPlanUpgradeModal.error && (
                <div className="mt-3 rounded-lg border border-[#f5c2c7] bg-[#fff5f6] px-3 py-2 text-[13px] text-[#b4232f]">
                  {offerPlanUpgradeModal.error}
                </div>
              )}

              <div className="mt-5 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    handleOfferPlanUpgradeModalAction('cancel').catch((error) => {
                      console.error('Failed to submit offer_plan_upgrade cancel action', error);
                    });
                  }}
                  disabled={offerPlanUpgradeModal.isSubmitting}
                  className={`rounded-xl border px-4 py-2 text-[13px] font-semibold transition-colors ${
                    offerPlanUpgradeModal.isSubmitting
                      ? 'cursor-wait border-[#e5e7eb] bg-[#f9fafb] text-[#9ca3af]'
                      : 'border-[#d7e3ff] bg-white text-[#4f6ea8] hover:border-[#97b9ff] hover:text-[#165BFB]'
                  }`}
                >
                  {offerPlanUpgradeModal.isSubmitting && offerPlanUpgradeModal.activeAction === 'cancel'
                    ? 'saving...'
                    : 'cancel'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    handleOfferPlanUpgradeModalAction('confirm').catch((error) => {
                      console.error('Failed to submit offer_plan_upgrade confirm action', error);
                    });
                  }}
                  disabled={offerPlanUpgradeModal.isSubmitting}
                  className={`agent-upgrade-button rounded-xl px-4 py-2 text-[13px] font-semibold text-white transition-opacity ${
                    offerPlanUpgradeModal.isSubmitting
                      ? 'cursor-wait bg-[#f4b8ca]'
                      : 'bg-gradient-to-r from-[#eb6f95] to-[#f0b175] hover:opacity-95'
                  }`}
                >
                  {offerPlanUpgradeModal.isSubmitting && offerPlanUpgradeModal.activeAction === 'confirm'
                    ? 'saving...'
                    : 'confirm upgrade'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {previewImage && (
          <motion.div
            className="agent-overlay fixed inset-0 z-[90] flex items-center justify-center bg-[#0f172acc] p-4 backdrop-blur-sm"
            onClick={handleCloseImagePreview}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="agent-dialog relative w-full max-w-4xl rounded-2xl border border-[#ccdcff] bg-[#f7faff] p-3 shadow-[0px_24px_56px_rgba(15,23,42,0.35)]"
              onClick={(event) => event.stopPropagation()}
              initial={{ opacity: 0, y: 16, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.96 }}
              transition={{ duration: 0.2 }}
              role="dialog"
              aria-modal="true"
              aria-label={previewImage.name || 'Uploaded image preview'}
            >
              <div className="mb-2 flex items-center justify-between gap-2">
                <span className="max-w-[80%] truncate text-[12px] font-semibold uppercase tracking-[0.08em] text-[#4f6ea8]">
                  {previewImage.name || 'Uploaded image'}
                </span>
                <button
                  type="button"
                  onClick={handleCloseImagePreview}
                  className="rounded-lg border border-[#d7e3ff] bg-white p-1.5 text-[#4f6ea8] transition-colors hover:border-[#97b9ff] hover:text-[#165BFB]"
                  aria-label="Close image preview"
                >
                  <svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <path d="M18 6 6 18M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="rounded-xl border border-[#d9e6ff] bg-white p-2">
                <img
                  src={previewImage.url}
                  alt={previewImage.name || 'Uploaded image'}
                  className="max-h-[75vh] w-full rounded-lg object-contain"
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Loading Indicator - Non-blocking */}
      {selectedSession?.type === 'vibe_site' && ((siteBriefTask && siteBriefTask.state !== 'completed') || siteBriefTaskPollingError) && (
        <div className="fixed bottom-52 left-1/2 z-50 max-w-md -translate-x-1/2 rounded-2xl bg-white px-5 py-3 text-sm shadow-lg" role="status" aria-live="polite">
          {siteBriefTaskPollingError ? 'Could not refresh website progress. Retrying…'
            : siteBriefTask?.state === 'failed' ? siteBriefTask.error
              : siteBriefTask?.state === 'completed' ? 'Website task completed.'
                : siteBriefTask?.state === 'queued' ? 'Website task queued…' : 'Processing website task…'}
        </div>
      )}
      {isLoading && (
        <motion.div
          className="fixed bottom-32 left-1/2 -translate-x-1/2 bg-white rounded-full shadow-lg px-5 py-3 flex items-center gap-3 z-50 loading-indicator-appear"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <svg className="animate-spin" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="#EDF0F5" strokeWidth="2.5" />
            <path d="M12 2C14.717 2 17.337 3.06458 19.2695 4.95867C21.2021 6.85276 22.3362 9.42975 22.3928 12.1455C22.4493 14.8612 21.4241 17.4813 19.5722 19.4518" stroke="#165BFB" strokeWidth="3" strokeLinecap="round" />
          </svg>
          <span className="text-[#333333] font-medium">Processing...</span>
        </motion.div>
      )}
    </motion.div>
  );
}

type MessageActionBarProps = {
  message: Message;
  onCopy: (message: Message) => void;
  onToggleReaction: (message: Message, action: MessageActionKey) => void;
  onDelete: (message: Message) => void;
  loading?: Record<MessageActionKey, boolean>;
  isCopied: boolean;
  actionsDisabled: boolean;
};

function MessageActionBar({
  message,
  onCopy,
  onToggleReaction,
  onDelete,
  loading,
  isCopied,
  actionsDisabled
}: MessageActionBarProps) {
  const messageIdLabel = String(message.id ?? '');
  const timestampLabel = message.timestamp ? formatMessageTimestamp(message.timestamp) : '';
  const durationLabel = typeof message.duration === 'number' ? formatMessageDuration(message.duration) : '';

  const likeActive = Boolean(message.isLiked);
  const dislikeActive = Boolean(message.isDisliked);

  const loadingState = {
    like: loading?.like ?? false,
    dislike: loading?.dislike ?? false,
    delete: loading?.delete ?? false
  };

  const baseButtonClass = 'agent-message-action flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs transition-colors';
  const activeButtonClass = 'bg-[#e6f0ff] border-[#165bfb] text-[#165bfb]';
  const inactiveButtonClass = 'bg-white border-[#e5e7eb] text-[#6b7280] hover:border-[#165bfb] hover:text-[#165bfb]';
  const disabledButtonClass = 'opacity-50 cursor-not-allowed pointer-events-none';
  const copyButtonClass = `${baseButtonClass} ${isCopied ? activeButtonClass : inactiveButtonClass}`;
  const infoPillClass = 'agent-message-meta flex items-center gap-1 rounded-full bg-[#f5f7fb] px-3 py-1 text-xs text-[#4b5563]';

  const isLikeDisabled = actionsDisabled || loadingState.like;
  const isDislikeDisabled = actionsDisabled || loadingState.dislike;
  const isDeleteDisabled = actionsDisabled || loadingState.delete;

  return (
    <div className="agent-message-actions mt-3 flex flex-wrap items-center gap-2">
      {messageIdLabel && (
        <span style={{display: 'none'}} className={infoPillClass} title={`Message ID: ${messageIdLabel}`}>
          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M7 7h10v10H7z" />
            <path d="M3 7h4v10H3zM17 7h4v10h-4z" />
          </svg>
          {messageIdLabel}
        </span>
      )}

      <button
        type="button"
        className={`${baseButtonClass} ${likeActive ? activeButtonClass : inactiveButtonClass} ${isLikeDisabled ? disabledButtonClass : ''}`}
        onClick={() => onToggleReaction(message, 'like')}
        disabled={isLikeDisabled}
      >
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill={likeActive ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 21s-6-4.35-6-8.5S8.686 4 12 7.5 18 4 18 12.5 12 21 12 21z" />
        </svg>
        {loadingState.like ? 'Saving...' : likeActive ? 'Liked' : 'Like'}
      </button>

      <button
        type="button"
        className={`${baseButtonClass} ${dislikeActive ? activeButtonClass : inactiveButtonClass} ${isDislikeDisabled ? disabledButtonClass : ''}`}
        onClick={() => onToggleReaction(message, 'dislike')}
        disabled={isDislikeDisabled}
      >
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill={dislikeActive ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 3s6 4.35 6 8.5S15.314 20 12 16.5 6 20 6 11.5 12 3 12 3z" />
        </svg>
        {loadingState.dislike ? 'Saving...' : dislikeActive ? 'Disliked' : 'Dislike'}
      </button>

      <button
        type="button"
        className={copyButtonClass}
        onClick={() => onCopy(message)}
      >
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
        </svg>
        {isCopied ? 'Copied' : 'Copy'}
      </button>

      {durationLabel && (
        <span className={infoPillClass} title={`Duration: ${durationLabel}`}>
          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 12 8 8" />
            <path d="M12 12h4" />
          </svg>
          {durationLabel}
        </span>
      )}

      {timestampLabel && (
        <span className={infoPillClass} title={timestampLabel}>
          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7v5l3 2" />
          </svg>
          {timestampLabel}
        </span>
      )}

      <button
        type="button"
        className={`${baseButtonClass} ${inactiveButtonClass} ${isDeleteDisabled ? disabledButtonClass : ''}`}
        onClick={() => onDelete(message)}
        disabled={isDeleteDisabled}
      >
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 6h18" />
          <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
          <path d="M10 11v6" />
          <path d="M14 11v6" />
        </svg>
        {loadingState.delete ? 'Removing...' : 'Delete'}
      </button>
    </div>
  );
}

// Suggestion Button Component matching the imported Figma design
type SuggestionButtonProps = { text: string; onClick: () => void };
function SuggestionButton({ text, onClick }: SuggestionButtonProps) {
  return (
    <motion.div
      className="relative rounded-[64px] cursor-pointer suggestion-button"
      onClick={onClick}
      whileHover={{ scale: 1.03, y: -2 }}
      whileTap={{ scale: 0.97 }}
      transition={{ duration: 0.2 }}
    >
      <div className="agent-suggestion-outline absolute border-2 border-[#4b81ff] border-solid inset-0 pointer-events-none rounded-[64px]" />
      <div className="flex flex-row items-center">
        <div className="box-border content-stretch flex flex-row gap-1 items-center justify-start px-3.5 py-2">
          <div className="overflow-clip relative rounded-[5.92932px] shrink-0 size-5">
            <div className="absolute bottom-[12.5%] left-[12.5%] right-[12.5%] top-[12.5%]">
              <svg
                className="block size-full"
                fill="none"
                preserveAspectRatio="none"
                viewBox="0 0 15 15"
              >
                <path
                  clipRule="evenodd"
                  d={svgHelpPaths.p6b0d00}
                  fill="#165BFB"
                  fillRule="evenodd"
                />
              </svg>
            </div>
          </div>
          <div className="font-normal text-[#165bfb] text-[14px] text-nowrap">
            <p className="leading-[20px] whitespace-pre">{text}</p>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// Assistant Icon Component with Animation
type AssistantIconProps = { messagesLength: number };
function AssistantIcon({ messagesLength }: AssistantIconProps) {
  const [isAnimating, setIsAnimating] = useState(false);

  // Trigger animation whenever messages change
  useEffect(() => {
    setIsAnimating(true);
    const timer = setTimeout(() => setIsAnimating(false), 800);
    return () => clearTimeout(timer);
  }, [messagesLength]);

  return (
    <motion.div
      className="bg-[#eef3ff] size-full flex items-center justify-center rounded-full"
      initial={{ scale: 0.8 }}
      animate={{ scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      <div className={`assistant-icon-container ${isAnimating ? 'animating' : ''}`}>
        {isAnimating && (
          <div className={`assistant-icon-pulse ${isAnimating ? 'animating' : ''}`} />
        )}
        <div className="size-[60%]">
          <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 19 19">
            <defs>
              <linearGradient id="botIconGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#165BFB" />
                <stop offset="100%" stopColor="#4087FF" />
              </linearGradient>
            </defs>
            <g>
              <path d={svgPaths.p12a13a40} fill="url(#botIconGradient)" className={isAnimating ? 'gradient-flow' : ''} />
              <path d={svgPaths.pa8077f0} fill="url(#botIconGradient)" className={isAnimating ? 'gradient-flow' : ''} />
              <path d={svgPaths.p35352600} fill="url(#botIconGradient)" className={isAnimating ? 'gradient-flow' : ''} />
            </g>
          </svg>
        </div>
      </div>
    </motion.div>
  );
}
